import CampaignInventory, { CampaignIcon } from '../components/curated-campaign/CampaignInventory.jsx';
import { CampaignAds } from '../components/curated-campaign/CampaignAds.jsx';
import './CampaignDetails.css';
import { useCompanyBilling } from '../components/CompanyBilling.jsx';
import { campaignSpendAccess, campaignBudgetAccess, campaignBudgetBaseline } from '../campaign-billing-controls.js';
import { CampaignBillingAccessDialog } from '../components/curated-campaign/CampaignBillingAccess.jsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  api,
  CURATED_CAMPAIGN_LIST_PAGE_SIZE,
  curatedCampaignManagementApi,
} from '../api.js';
import { IconChevron, IconClose, IconExternal } from '../components/icons.jsx';
import Select from '../components/Select.jsx';
import { CampaignSettings, CampaignStatePill, campaignStatusTone } from '../components/curated-campaign/CampaignSummaryCard.jsx';
import { Button } from '../components/primitives.jsx';
import { CampaignConfirmationDialog, CampaignModal, CampaignSettingsDrawer } from '../components/curated-campaign/CampaignOverlays.jsx';
import { useDocumentNoticeDismissal, campaignNoticeKey } from '../components/DismissibleNotice.jsx';
import Beacon from '../components/Beacon.jsx';
import {
  CURATED_META_PUBLISH_NOT_STARTED_READ_LIMIT,
  CuratedMetaPublishProgress,
  useCuratedMetaPublishProgress,
  useCuratedMetaPublishRetryWait,
  yieldCuratedMetaPublishProgressPaint,
} from '../components/CuratedMetaPublishProgress.jsx';
import {
  ConfirmationDialog,
  useConfirmationDialog,
} from '../components/ConfirmationDialog.jsx';
import {
  CampaignManagementControls,
  campaignManagementActionsForPolicy,
  campaignManagementActionBlocker,
  campaignManagementRetryKey,
  campaignDeleteBlockerMessage,
  useCampaignDeleteRetryWait,
  normalizeCampaignDeletionResponse,
  normalizeCampaignManagementResponse,
  unobservableArchiveNotice,
} from '../components/curated-campaign/CampaignManagementControls.jsx';
import { hasNamedTenantAction } from '../product-policy.js';
import { safeFailureMessage } from '../safe-failure.js';
import {
  CONTINUITY_ORIGINAL_UNPROVABLE_CODE,
  CONTINUITY_REFRESHING_CODE,
  continuityRefetchDelayMs,
  createContinuityRefetchScheduler,
  detailContinuityRefreshing,
  summaryBatchHasUnprovableOriginal,
  summaryBatchIsRefreshing,
} from '../continuity-refresh.js';
import {
  campaignDetailReadCoordinator,
  campaignDetailReadScopeKey,
  isCurrentCampaignDetailRead,
} from '../campaign-detail-read-coordinator.js';
import {
  campaignManagementAllowsMetaControls,
  campaignMetaAccessRecoverySnapshot,
  executeCampaignMetaAccessRecovery,
} from '../campaign-meta-access-recovery.js';
import {
  curatedMetaPublishConfirmation,
  curatedMetaPublishRecovery,
  curatedMetaPublishScopeKey,
  curatedMetaPublishSnapshotFromDetail,
  resumeCuratedPausedCreation,
} from '../curated-meta-publish.js';

const CURATED_CAMPAIGN_KIND = 'curated_ads_v1';
const CAMPAIGN_MANAGEMENT_LIST_INVALIDATED_EVENT = 'beyondleads:campaign-management-list-invalidated';
const CAMPAIGN_MANAGEMENT_LIST_INVALIDATION_LIMIT = 64;
const campaignManagementListInvalidationVersions = new Map();
const OPERATIONAL_STATES = new Set([
  'draft',
  'awaiting_approval',
  'approved',
  'publishing',
  'paused',
  'active',
  'partial',
  'failed',
  'completed',
  'archived',
]);
const SETUP_STATES = new Set(['draft', 'awaiting_approval', 'approved']);
const CAMPAIGN_DETAIL_STATES = new Set([
  'publishing', 'paused', 'active', 'partial', 'failed', 'completed', 'archived',
]);
const META_CREATION_PROGRESS_STATES = new Set(['publishing', 'paused', 'partial', 'failed']);
const DATE_PRESETS = Object.freeze([
  Object.freeze({ value: 'today', label: 'Today' }),
  Object.freeze({ value: 'last_7d', label: 'Last 7 days' }),
  Object.freeze({ value: 'last_30d', label: 'Last 30 days' }),
  Object.freeze({ value: 'maximum', label: 'Lifetime' }),
]);
const CAMPAIGN_ROW_DATE_PRESET = 'last_30d';
const CAMPAIGN_ROW_SUMMARY_BATCH_LIMIT = 50;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const CAMPAIGN_ARCHIVE_VIEWS = Object.freeze([
  Object.freeze({ id: 'active', label: 'Current', archived: 'exclude' }),
  Object.freeze({ id: 'all', label: 'All', archived: 'include' }),
  Object.freeze({ id: 'archived', label: 'Archived', archived: 'only' }),
]);
const DEFINITIVE_MANAGEMENT_REJECTION_STATUSES = new Set([
  400, 401, 403, 404, 409, 410, 412, 422,
]);
const CAMPAIGN_MANAGEMENT_ACTIONS = new Set(['archive', 'restore', 'delete']);
const LIFECYCLE_CONFIRMATIONS = Object.freeze({
  activate: 'Activate this campaign on Meta? BeyondLeads will prepare the exact approved ads and shared ad set while the campaign remains paused, then activate the campaign last. Delivery and spending may begin once Meta permits delivery.',
  pause: 'Pause this campaign on Meta? Delivery will stop at the campaign level. Child configured readiness remains unchanged for a safe resume.',
  resume: 'Resume this campaign on Meta? BeyondLeads will verify the shared ad set and enabled approved ads. Individually paused ads remain paused. With no enabled ads, delivery stays stopped; otherwise delivery and spending may begin once Meta permits delivery.',
});
const ACTIONS = Object.freeze({
  activate: Object.freeze({ productAction: 'campaign.activate', verb: 'Activate campaign', confirmation: LIFECYCLE_CONFIRMATIONS.activate }),
  pause: Object.freeze({ productAction: 'campaign.pause', verb: 'Pause campaign', confirmation: LIFECYCLE_CONFIRMATIONS.pause }),
  resume: Object.freeze({ productAction: 'campaign.resume', verb: 'Resume campaign', confirmation: LIFECYCLE_CONFIRMATIONS.resume }),
  budget: Object.freeze({ productAction: 'campaign.budget.update', verb: 'Update budget', confirmation: 'Update the campaign-level budget on Meta?' }),
});
const OPERATION_ACTIONS = Object.freeze({
  campaign_activate: 'activate',
  campaign_pause: 'pause',
  campaign_resume: 'resume',
  campaign_budget_update: 'budget',
});
const RETRY_TOKEN_PATTERN = /^[A-Za-z0-9:_-]{16,200}$/;
const PROVIDER_STATUS_PATTERN = /^[A-Z][A-Z0-9_]{0,63}$/;
const HIERARCHY_RECONCILIATION_STATES = new Set([
  'confirmed', 'partial', 'pending', 'failed', 'stale', 'unavailable',
]);
const MAX_HIERARCHY_COUNT = 100;
const CAMPAIGN_REAUTH_ATTENTION = Object.freeze({
  title: 'Reconnect required',
  body: 'Reconnect Meta Ads before managing this campaign.',
});
const CAMPAIGN_ATTENTION_PRESENTATIONS = new Map([
  ['meta_authorization_check_required', Object.freeze({
    title: 'Meta connection verification required',
    body: 'Ask a company owner or admin to verify the Meta Ads connection, then refresh this campaign. The saved campaign and its durable status are unchanged.',
  })],
  ['meta_authorization_check_unavailable', Object.freeze({
    title: 'Meta connection temporarily unavailable',
    body: 'The Meta Ads connection could not be verified right now. The saved campaign and its durable status are unchanged; try Refresh status again shortly.',
  })],
  ['meta_provider_timeout', Object.freeze({
    title: 'Meta response timed out',
    body: 'Meta did not respond in time while checking live campaign status. The saved campaign and its durable status are unchanged; try Refresh status again shortly.',
  })],
  ['meta_reauthorization_required', CAMPAIGN_REAUTH_ATTENTION],
  ['meta_connection_reauth_required', CAMPAIGN_REAUTH_ATTENTION],
  ['meta_campaign_credential_continuity_mismatch', Object.freeze({
    title: 'Original Meta account required',
    body: 'Reconnect the Meta account that owns this campaign before continuing. The saved campaign and its durable status are unchanged, and campaign controls remain blocked.',
  })],
  ['meta_campaign_credential_continuity_identity_unavailable', Object.freeze({
    title: 'Original Meta account required',
    body: 'BeyondLeads could not verify that the current Meta login owns this campaign. Reconnect the original Meta account or contact support. The saved campaign is unchanged.',
  })],
  ['meta_campaign_credential_continuity_binding_invalid', Object.freeze({
    title: 'Campaign ownership needs review',
    body: 'The approved Meta campaign ownership record could not be confirmed. Contact support before changing this campaign; the saved campaign is unchanged.',
  })],
  ['meta_campaign_credential_continuity_not_found', Object.freeze({
    title: 'Campaign ownership needs review',
    body: 'The approved Meta campaign ownership record is unavailable. Contact support before changing this campaign; the saved campaign is unchanged.',
  })],
  ['meta_campaign_credential_continuity_ambiguous', Object.freeze({
    title: 'Meta connection needs review',
    body: 'More than one current Meta connection could match this campaign. Disconnect the unused connection or contact support. The saved campaign is unchanged.',
  })],
  ['meta_campaign_credential_continuity_stale', Object.freeze({
    title: 'Verified Meta connection required',
    body: 'The matching Meta connection is not current. Verify or reconnect Meta Ads, refresh its resources, and try again. The saved campaign is unchanged.',
  })],
  ['meta_campaign_credential_continuity_required', Object.freeze({
    title: 'Verified Meta connection required',
    body: 'A verified Meta connection that owns this campaign is required. Verify or reconnect Meta Ads, refresh its resources, and try again. The saved campaign is unchanged.',
  })],
  ['meta_campaign_credential_continuity_unavailable', Object.freeze({
    title: 'Campaign ownership check unavailable',
    body: 'Campaign ownership could not be verified right now. Try again shortly; the saved campaign and its durable status are unchanged.',
  })],
  ['meta_campaign_credential_continuity_repository_unavailable', Object.freeze({
    title: 'Campaign ownership check unavailable',
    body: 'Campaign ownership could not be verified right now. Try again shortly; the saved campaign and its durable status are unchanged.',
  })],
  ['meta_campaign_credential_continuity_response_invalid', Object.freeze({
    title: 'Campaign ownership check unavailable',
    body: 'Campaign ownership could not be verified safely. Try again shortly or contact support; the saved campaign is unchanged.',
  })],
  // OGC-488: Meta refuses to inspect the token this campaign was published
  // with. Every proof starts from that original version, so reconnecting,
  // retrying or Verify access cannot restore it; the copy says what can.
  [CONTINUITY_ORIGINAL_UNPROVABLE_CODE, Object.freeze({
    title: 'Republish needed',
    body: 'Meta can no longer confirm the Meta login this campaign was published with, so BeyondLeads cannot show its live status or spend or change it, and reconnecting will not restore that. Nothing in Meta was changed: if it is running, it keeps delivering and sending leads until you pause it in Meta Ads Manager. To manage it here, publish a new campaign.',
  })],
  // OGC-478: a Meta resource refresh is queued or running. The page re-checks
  // only a bounded number of times, and the refresh can still end in a refusal
  // an owner or admin must clear, so the copy promises neither recovery nor a
  // further re-check (the read-only note says whether one is still coming).
  ['meta_campaign_credential_continuity_refreshing', Object.freeze({
    title: 'Refreshing Meta access',
    body: 'BeyondLeads is refreshing the Meta resources this campaign uses. If controls do not return shortly, refresh the page or ask a company owner or admin to open it. The saved campaign and its durable status are unchanged.',
  })],
]);

const META_CAMPAIGN_TARGET_PRESENTATIONS = new Map([
  ['ready', Object.freeze({
    lifecycle: 'unpublished',
    reasonCode: 'meta_campaign_target_ready',
    title: 'Meta destination ready',
    body: 'The saved Meta destination is available through the current authorization.',
    statusLabel: 'Ready',
  })],
  ['disconnected', Object.freeze({
    lifecycle: 'unpublished',
    reasonCode: 'meta_campaign_target_disconnected',
    title: 'Meta reauthorization required',
    body: 'The saved destination is retained. Reauthorize Meta with access to the same assets before continuing.',
    statusLabel: 'Reauthorize',
  })],
  ['verification_required', Object.freeze({
    lifecycle: 'unpublished',
    reasonCode: 'meta_campaign_target_verification_required',
    title: 'Meta verification required',
    body: 'Verify the current Meta authorization and refresh its resources before continuing. The saved destination will not be changed.',
    statusLabel: 'Verify access',
  })],
  ['check_unavailable', Object.freeze({
    lifecycle: 'unpublished',
    reasonCode: 'meta_campaign_target_check_unavailable',
    title: 'Meta destination check unavailable',
    body: 'The saved destination could not be verified safely right now. Try again shortly; BeyondLeads will not use or replace it while this check is unavailable.',
    statusLabel: 'Try again',
  })],
  ['scope_missing', Object.freeze({
    lifecycle: 'unpublished',
    reasonCode: 'meta_campaign_target_scope_missing',
    title: 'Additional Meta access required',
    body: 'Reauthorize Meta with campaign and lead access for this saved destination. BeyondLeads will not substitute different assets.',
    statusLabel: 'Access required',
  })],
  ['resource_unavailable', Object.freeze({
    lifecycle: 'unpublished',
    reasonCode: 'meta_campaign_target_resource_unavailable',
    title: 'Saved Meta destination unavailable',
    body: 'The current Meta authorization cannot access every saved destination asset. Contact a company administrator before changing this campaign.',
    statusLabel: 'Needs review',
  })],
  ['ambiguous', Object.freeze({
    lifecycle: 'unpublished',
    reasonCode: 'meta_campaign_target_authorization_ambiguous',
    title: 'Meta authorization needs review',
    body: 'More than one authorization could match this saved destination. Contact a company administrator; BeyondLeads will not choose one automatically.',
    statusLabel: 'Needs review',
  })],
  ['published_continuity', Object.freeze({
    lifecycle: 'published_continuity',
    reasonCode: 'meta_campaign_target_published_continuity',
    title: 'Published Meta destination retained',
    body: 'This succeeded publication continues through the stricter original-authorization continuity check. Reauthorization does not retarget it.',
    statusLabel: 'Published continuity',
  })],
  ['partial_failed_pinned', Object.freeze({
    lifecycle: 'partial_failed',
    reasonCode: 'meta_campaign_target_partial_failed_pinned',
    title: 'Partial Meta publication pinned',
    body: 'This partial publication stays pinned to its original Meta authorization and confirmed checkpoints. Resume only the original operation; reauthorization does not retarget it.',
    statusLabel: 'Original operation',
  })],
  ['publication_unsettled', Object.freeze({
    lifecycle: 'unsettled',
    reasonCode: 'meta_campaign_target_publication_unsettled',
    title: 'Meta publication still settling',
    body: 'This publication remains pinned to its original Meta authorization while the existing operation settles. A new authorization cannot replace its checkpoints.',
    statusLabel: 'Operation pending',
  })],
]);
const MAX_META_TARGET_LABEL_LENGTH = 120;
const UNSAFE_META_TARGET_LABEL = /(?:\bBearer\b|access[_ -]?token|refresh[_ -]?token|client[_ -]?secret|service[_ -]?role|private[_ -]?key|password|sk-[a-z0-9_-]{12,}|sb_secret_[A-Za-z0-9_-]{8,}|eyJ[a-z0-9_-]{20,}|\/Users\/|\/app\/|node_modules|^[0-9a-f]{8}-[0-9a-f-]{27,}$|^[0-9]{6,}$|^(?:act_|page_|form_|ig_|credential[_-])[a-z0-9._:-]{4,}$)/i;

function safeMetaTargetLabel(value, fallback) {
  const label = exactText(value);
  if (
    !label
    || label.length > MAX_META_TARGET_LABEL_LENGTH
    || /[\u0000-\u001f\u007f]/.test(label)
    || UNSAFE_META_TARGET_LABEL.test(label)
  ) return fallback;
  return label;
}

function normalizeMetaTargetDestination(value) {
  const destination = value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : {};
  const labelFor = (key, fallback) => Object.freeze({
    label: safeMetaTargetLabel(destination[key]?.label, fallback),
  });
  return Object.freeze({
    adAccount: labelFor('adAccount', 'Unavailable'),
    page: labelFor('page', 'Unavailable'),
    instantForm: labelFor('instantForm', 'Unavailable'),
    instagramIdentity: destination.instagramIdentity == null
      ? null
      : labelFor('instagramIdentity', 'Unavailable'),
  });
}

export function normalizeMetaCampaignTargetPresentation(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const health = exactText(value.health).toLowerCase();
  const presentation = META_CAMPAIGN_TARGET_PRESENTATIONS.get(health);
  if (
    !presentation
    || exactText(value.lifecycle).toLowerCase() !== presentation.lifecycle
    || exactText(value.reasonCode) !== presentation.reasonCode
  ) return null;
  return Object.freeze({
    lifecycle: presentation.lifecycle,
    health,
    reasonCode: presentation.reasonCode,
    title: presentation.title,
    body: presentation.body,
    statusLabel: presentation.statusLabel,
    destination: normalizeMetaTargetDestination(value.destination),
  });
}

function tenantHeaders(companyId) {
  return { headers: { 'x-company-id': companyId } };
}

function exactText(value, fallback = '') {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

export function shouldShowCuratedMetaCreationProgress(campaignStatus, progress) {
  return META_CREATION_PROGRESS_STATES.has(exactText(campaignStatus).toLowerCase())
    && Boolean(progress?.state)
    && progress.state !== 'not_started';
}

// OGC-409: an `approved` campaign whose Review seal has already committed a
// binding and a publish intent — but whose durable `campaigns.status` has not
// yet flipped to `publishing` (the narrow seal->claim window inside the
// create-paused request, before `transitionCuratedCampaign` commits) — has a
// genuinely live paused-creation attempt in flight. During that window the
// authorization outcome that drives `metaTarget.lifecycle` still reads
// `unpublished` (a freshly sealed `pending` intent has no provider objects
// yet), so the server's unpublished projection now surfaces the live
// attempt's `publishStatus` specifically for this gap (see
// `liveCuratedMetaPublishIntent`/`providerProjection` in
// `curated-campaign-operations-service.js`) — `availability` stays
// `not_published` and no provider object, timestamp, or failure is exposed.
// A legacy `approved` campaign with no live intent (OGC-344) still reports
// `publishStatus: null` and must not be polled.
const LIVE_PUBLISH_INTENT_STATUSES = new Set(['pending', 'in_progress']);
export function curatedCampaignHasLivePublishIntent(provider) {
  return LIVE_PUBLISH_INTENT_STATUSES.has(exactText(provider?.publishStatus).toLowerCase());
}

// OGC-404: the Details view polls useCuratedMetaPublishProgress for the
// bound campaign/workflow/approval the whole time creation is durable-visible
// (publishing/paused/partial/failed), plus for the duration of a Resume/retry
// click regardless of the durable status shown at that instant. A Resume
// click can observe a stale or transient `failed` checkpoint the server is
// actively overwriting mid-request — the same race OGC-400 fixed on the
// create/setup path with `continueOnFailed: busy === 'publish'` (see
// CuratedCampaignSetup.jsx). This mirrors that fix for the Details consumer:
// only while a publish mutation (create/resume/retry) is pending does a
// `failed` read keep the poll timer armed, so a transient bad read cannot
// permanently freeze the progress block on stale counts. Factored out so it
// is unit-testable without rendering the hook.
//
// OGC-409 extends this with a third case: an `approved` campaign that carries
// evidence of a live paused-creation intent (see
// `curatedCampaignHasLivePublishIntent` above) also polls, with
// `continueOnNotStarted` set — the progress endpoint can legitimately answer
// `not_started` for one beat right at the seal boundary before the intent's
// own `running` projection catches up, and the poll must not stop there.
// The hook bounds that continuation (OGC-438), so a sealed-but-never-claimed
// intent settles into the static approved view instead of polling forever;
// an executor that claims only after the bound is picked up on the next
// open of Details or on a Resume click.
export function curatedCampaignPublishProgressPollingOptions(campaignStatus, currentMutation = {}, provider = null) {
  const status = exactText(campaignStatus).toLowerCase();
  const publishMutationPending = currentMutation?.action === 'publish'
    && currentMutation?.status === 'pending';
  const approvedWithLiveIntent = status === 'approved' && curatedCampaignHasLivePublishIntent(provider);
  return Object.freeze({
    enabled: Boolean(META_CREATION_PROGRESS_STATES.has(status) || publishMutationPending || approvedWithLiveIntent),
    continueOnNotStarted: Boolean(status === 'publishing' || publishMutationPending || approvedWithLiveIntent),
    continueOnFailed: Boolean(publishMutationPending),
    // OGC-438: the bound applies to the static approved+live-intent case; a
    // Resume/retry request in flight is the click→claim window and is not
    // bounded (its return restarts the loop either way).
    notStartedReadLimit: publishMutationPending ? Infinity : CURATED_META_PUBLISH_NOT_STARTED_READ_LIMIT,
  });
}

// OGC-409: the campaign row can still read a stale status (e.g. `approved`)
// for a few seconds after the durable status has actually moved on — the
// seal->claim window inside the create-paused request, where a
// paused-creation intent has already sealed and
// `/meta-publish-progress` reports the executor's claim (e.g. `publishing`)
// before `campaigns.status` itself has flipped. Rather than bounding the
// reconnect to a fixed retry count — which can run out before the executor
// actually claims the row (measured 10-30s across staging runs, far past a
// 3-attempt/750ms budget) — drive it off the campaign status the
// `/meta-publish-progress` poll itself observes: once that observation
// disagrees with the local detail projection, there is a real status to
// reconcile to. Adds no requests beyond the existing progress poll. Returns
// '' whenever either value is empty or the two already agree, so it cannot
// fire for a campaign whose local status simply matches the polled one (the
// ordinary case of opening Details on an already-current campaign, or after
// a refresh has just caught the local projection up). Factored out so the
// transition is unit-testable without rendering the hook.
export function curatedCampaignObservedStatusReconciliationKey(detailCampaignStatus, observedCampaignStatus) {
  const local = exactText(detailCampaignStatus).toLowerCase();
  const observed = exactText(observedCampaignStatus).toLowerCase();
  if (!local || !observed || local === observed) return '';
  return `${local}->${observed}`;
}

// OGC-409: after a reconciliation refresh, decide whether the disagreement
// this refresh was chasing is still open. This is deliberately NOT just
// `Boolean(curatedCampaignObservedStatusReconciliationKey(refreshed, observed))`:
// that key also reads '' when the refreshed status is empty, and a refreshed
// status can be empty for two very different reasons — the local status
// genuinely caught up to something the caller already folded away (never the
// case here, since the caller only calls this after a refresh), or the
// refresh itself failed. `loadDetail` swallows its own request errors and
// resolves `null` rather than throwing (see its catch branch), so a
// transient failure surfaces here as `refreshedDetailCampaignStatus` being
// null/undefined/empty — not as a rejected promise. Collapsing that into "no
// disagreement" is wrong: nothing was reconciled, so it must still count as
// diverged and retry, mirroring how the terminal-reconciliation effect
// treats an empty `refreshedStatus` as not-yet-reconciled.
export function curatedCampaignObservedStatusStillDiverged(refreshedDetailCampaignStatus, observedCampaignStatus) {
  const refreshedLocal = exactText(refreshedDetailCampaignStatus).toLowerCase();
  if (!refreshedLocal) return true;
  const observed = exactText(observedCampaignStatus).toLowerCase();
  return refreshedLocal !== observed;
}

export function campaignManagementListInvalidationVersion(companyId) {
  const normalizedCompanyId = exactText(companyId);
  return normalizedCompanyId
    ? campaignManagementListInvalidationVersions.get(normalizedCompanyId) || 0
    : 0;
}

export function invalidateCampaignManagementList(companyId) {
  const normalizedCompanyId = exactText(companyId);
  if (!normalizedCompanyId) return 0;
  const version = campaignManagementListInvalidationVersion(normalizedCompanyId) + 1;
  campaignManagementListInvalidationVersions.delete(normalizedCompanyId);
  campaignManagementListInvalidationVersions.set(normalizedCompanyId, version);
  while (campaignManagementListInvalidationVersions.size > CAMPAIGN_MANAGEMENT_LIST_INVALIDATION_LIMIT) {
    campaignManagementListInvalidationVersions.delete(
      campaignManagementListInvalidationVersions.keys().next().value,
    );
  }
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent(CAMPAIGN_MANAGEMENT_LIST_INVALIDATED_EVENT, {
      detail: { companyId: normalizedCompanyId, version },
    }));
  }
  return version;
}

function titleCase(value) {
  return exactText(value, 'unavailable')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function operationLabel(value) {
  const labels = {
    activate: 'Activate campaign',
    campaign_activate: 'Activate campaign',
    pause: 'Pause campaign',
    campaign_pause: 'Pause campaign',
    resume: 'Resume campaign',
    campaign_resume: 'Resume campaign',
    budget: 'Budget update',
    campaign_budget_update: 'Budget update',
  };
  return labels[value] || 'Unavailable';
}

export function campaignLifecycleConfirmation(action) {
  return LIFECYCLE_CONFIRMATIONS[action] || '';
}

function confirmationConsequence(value) {
  const message = typeof value === 'string' ? value.trim() : '';
  const questionEnd = message.indexOf('?');
  return questionEnd >= 0 ? message.slice(questionEnd + 1).trim() : message;
}

function campaignLifecycleDialogOptions(action, description) {
  const labels = {
    activate: 'Activate campaign',
    pause: 'Pause campaign',
    resume: 'Resume campaign',
    budget: 'Update budget',
  };
  const confirmLabel = labels[action];
  if (!confirmLabel || !description) return null;
  return {
    eyebrow: 'Meta campaign control',
    title: action === 'budget' ? 'Update campaign budget?' : `${action[0].toUpperCase()}${action.slice(1)} this campaign?`,
    description: confirmationConsequence(description),
    confirmLabel,
    tone: 'warning',
  };
}

function hierarchyCount(value) {
  return Number.isSafeInteger(value) && value >= 0 && value <= MAX_HIERARCHY_COUNT
    ? value
    : null;
}

function hierarchyStatus(value) {
  const normalized = typeof value === 'string' ? value.trim().toUpperCase() : '';
  return PROVIDER_STATUS_PATTERN.test(normalized) ? normalized : null;
}

function hierarchyRecord(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function hierarchyStatusCounts(value) {
  if (!Array.isArray(value)) return [];
  const totals = new Map();
  for (const item of value.slice(0, 20)) {
    const row = hierarchyRecord(item);
    const status = hierarchyStatus(row.status);
    const count = hierarchyCount(row.count);
    if (!status || count === null) continue;
    const total = (totals.get(status) || 0) + count;
    if (total > MAX_HIERARCHY_COUNT) return [];
    totals.set(status, total);
  }
  return [...totals.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([status, count]) => ({ status, count }));
}

function hierarchyTimestamp(value) {
  const normalized = exactText(value);
  return normalized.length <= 64 && Number.isFinite(Date.parse(normalized))
    ? normalized
    : '';
}

export function normalizeCampaignHierarchy(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const expected = hierarchyRecord(value.expected);
  const confirmed = hierarchyRecord(value.confirmed);
  const configured = hierarchyRecord(value.configured);
  const effective = hierarchyRecord(value.effective);
  const reconciliationStatus = exactText(value.reconciliationStatus).toLowerCase();
  return {
    expected: {
      campaignCount: hierarchyCount(expected.campaignCount),
      adSetCount: hierarchyCount(expected.adSetCount),
      adCount: hierarchyCount(expected.adCount),
    },
    confirmed: {
      campaignCount: hierarchyCount(confirmed.campaignCount),
      adSetCount: hierarchyCount(confirmed.adSetCount),
      adCount: hierarchyCount(confirmed.adCount),
    },
    configured: {
      campaignStatus: hierarchyStatus(configured.campaignStatus),
      adSetStatus: hierarchyStatus(configured.adSetStatus),
      activeAdCount: hierarchyCount(configured.activeAdCount),
      pausedAdCount: hierarchyCount(configured.pausedAdCount),
      otherAdCount: hierarchyCount(configured.otherAdCount),
      childrenReady: typeof configured.childrenReady === 'boolean'
        ? configured.childrenReady
        : null,
    },
    effective: {
      campaignStatus: hierarchyStatus(effective.campaignStatus),
      adSetStatus: hierarchyStatus(effective.adSetStatus),
      adStatusCounts: hierarchyStatusCounts(effective.adStatusCounts),
    },
    mappingConfirmed: typeof value.mappingConfirmed === 'boolean'
      ? value.mappingConfirmed
      : null,
    reconciliationStatus: HIERARCHY_RECONCILIATION_STATES.has(reconciliationStatus)
      ? reconciliationStatus
      : 'unavailable',
    checkedAt: hierarchyTimestamp(value.checkedAt),
  };
}

function quantityLabel(value, singular, plural = `${singular}s`) {
  return value === null || value === undefined
    ? `Unavailable ${plural}`
    : `${value} ${value === 1 ? singular : plural}`;
}

function hierarchyObjectCountLabel(value = {}) {
  return [
    quantityLabel(value.campaignCount, 'campaign'),
    quantityLabel(value.adSetCount, 'shared ad set'),
    quantityLabel(value.adCount, 'ad'),
  ].join(' · ');
}

function hierarchyConfiguredAdsLabel(value = {}) {
  if ([value.activeAdCount, value.pausedAdCount, value.otherAdCount].every((count) => count == null)) {
    return 'Unavailable';
  }
  return [
    `${value.activeAdCount ?? 'Unavailable'} active`,
    `${value.pausedAdCount ?? 'Unavailable'} paused`,
    `${value.otherAdCount ?? 'Unavailable'} other`,
  ].join(' · ');
}

function hierarchyEffectiveAdsLabel(value = []) {
  return value.length
    ? value.map(({ status, count }) => `${titleCase(status)} ${count}`).join(' · ')
    : 'Unavailable';
}

function hierarchyCountsMatch(hierarchy) {
  if (!hierarchy) return false;
  const keys = ['campaignCount', 'adSetCount', 'adCount'];
  return keys.every((key) => (
    hierarchy.expected[key] !== null
    && hierarchy.confirmed[key] === hierarchy.expected[key]
  ));
}

function hierarchyNeedsAttention(hierarchy, campaignStatus) {
  if (!hierarchy) return false;
  return hierarchy.mappingConfirmed !== true
    || !hierarchyCountsMatch(hierarchy)
    || ['partial', 'pending', 'failed', 'stale', 'unavailable'].includes(
      hierarchy.reconciliationStatus,
    )
    || (campaignStatus === 'active' && hierarchy.configured.childrenReady !== true);
}

function hierarchyFailureGuidance(hierarchy, campaignStatus) {
  if (!hierarchy) return '';
  if (hierarchy.mappingConfirmed !== true || !hierarchyCountsMatch(hierarchy)) {
    return 'The Meta campaign hierarchy no longer matches the exact approved campaign, shared ad set, and ads. Refresh before using a lifecycle control.';
  }
  if (hierarchy.reconciliationStatus === 'pending') {
    return 'Meta is still confirming the exact approved campaign hierarchy. Refresh before retrying this action.';
  }
  if (['stale', 'unavailable'].includes(hierarchy.reconciliationStatus)) {
    return 'The exact approved Meta hierarchy is temporarily unavailable or stale. Refresh before using a lifecycle control.';
  }
  if (campaignStatus === 'active' && hierarchy.configured.childrenReady !== true) {
    return 'The campaign is not fully ready: its exact approved child hierarchy is not configured for delivery. Refresh before using a lifecycle control.';
  }
  return 'Meta confirmed only part of the exact approved hierarchy. The campaign remains in its safe durable state; retry continues only incomplete work.';
}

export function claimLifecycleMutation(inFlight, requestScope) {
  const normalizedScope = exactText(requestScope);
  if (!inFlight || typeof inFlight !== 'object' || !normalizedScope || exactText(inFlight.current)) {
    return false;
  }
  inFlight.current = normalizedScope;
  return true;
}

export function releaseLifecycleMutation(inFlight, requestScope) {
  const normalizedScope = exactText(requestScope);
  if (
    inFlight
    && typeof inFlight === 'object'
    && normalizedScope
    && inFlight.current === normalizedScope
  ) {
    inFlight.current = '';
    return true;
  }
  return false;
}

export function normalizeCuratedCampaignList(value) {
  return (Array.isArray(value) ? value : [])
    .filter((campaign) => (
      campaign?.id
      && OPERATIONAL_STATES.has(campaign.status)
      && campaign.campaignKind === CURATED_CAMPAIGN_KIND
    ));
}

export function campaignArchiveFilter(view) {
  return CAMPAIGN_ARCHIVE_VIEWS.find(({ id }) => id === view)?.archived || 'exclude';
}

export function campaignRowSummaryPath(campaignIds, datePreset = CAMPAIGN_ROW_DATE_PRESET) {
  const ids = campaignRowSummaryBatchIds(campaignIds)
    .sort((left, right) => left.localeCompare(right));
  if (!ids.length || !datePreset) return '';
  return `/api/saas/campaigns/row-summaries?campaignIds=${ids.map(encodeURIComponent).join(',')}&datePreset=${encodeURIComponent(datePreset)}`;
}

export function campaignRowSummaryBatchIds(campaignIds) {
  const ids = [];
  const seen = new Set();
  for (const value of Array.isArray(campaignIds) ? campaignIds : []) {
    if (
      typeof value !== 'string'
      || value.trim() !== value
      || !UUID_PATTERN.test(value)
      || seen.has(value)
    ) continue;
    seen.add(value);
    ids.push(value);
    if (ids.length === CAMPAIGN_ROW_SUMMARY_BATCH_LIMIT) break;
  }
  return ids;
}

function campaignListResponseError() {
  const error = new Error('Campaign list response is invalid.');
  error.code = 'curated_campaign_list_page_invalid';
  return error;
}

function campaignListPagePresentation(items, {
  count = items.length,
  hasNext = false,
  limit = CURATED_CAMPAIGN_LIST_PAGE_SIZE,
  nextCursor = null,
  pageIndex = 0,
} = {}) {
  const startIndex = count ? (pageIndex * limit) + 1 : 0;
  const endIndex = count ? startIndex + count - 1 : 0;
  const rangeLabel = count === 0
    ? pageIndex > 0 ? `No campaigns on page ${pageIndex + 1}` : '0 campaigns'
    : count === 1
      ? `Showing campaign ${startIndex}`
      : `Showing campaigns ${startIndex}–${endIndex}`;
  const summaryRangeLabel = count === 0
    ? 'campaigns on this page'
    : count === 1
      ? `campaign ${startIndex}`
      : `campaigns ${startIndex}–${endIndex}`;
  return Object.freeze({
    items: Object.freeze(items),
    count,
    limit,
    pageIndex,
    hasPrevious: pageIndex > 0,
    hasNext,
    nextCursor,
    pageLabel: `Page ${pageIndex + 1}`,
    rangeLabel,
    summaryRangeLabel,
  });
}

export function normalizeCampaignListPageResponse(response, {
  expectedLimit = CURATED_CAMPAIGN_LIST_PAGE_SIZE,
  pageIndex = 0,
} = {}) {
  if (
    !response
    || typeof response !== 'object'
    || Array.isArray(response)
    || !Array.isArray(response.campaigns)
    || !response.page
    || typeof response.page !== 'object'
    || Array.isArray(response.page)
    || !Number.isSafeInteger(pageIndex)
    || pageIndex < 0
  ) throw campaignListResponseError();
  const pageKeys = Object.keys(response.page).sort();
  const expectedKeys = ['count', 'hasNext', 'limit', 'nextCursor'];
  const campaigns = normalizeCuratedCampaignList(response.campaigns);
  const { count, hasNext, limit, nextCursor } = response.page;
  const validCursor = nextCursor === null || (
    typeof nextCursor === 'string'
    && nextCursor.length > 0
    && nextCursor.length <= 512
    && /^[A-Za-z0-9_-]+$/.test(nextCursor)
  );
  if (
    pageKeys.length !== expectedKeys.length
    || !expectedKeys.every((key, index) => pageKeys[index] === key)
    || limit !== expectedLimit
    || !Number.isSafeInteger(count)
    || count < 0
    || count > limit
    || count !== response.campaigns.length
    || campaigns.length !== response.campaigns.length
    || typeof hasNext !== 'boolean'
    || !validCursor
    || hasNext !== Boolean(nextCursor)
    || (hasNext && count !== limit)
  ) throw campaignListResponseError();
  return campaignListPagePresentation(campaigns, {
    count,
    hasNext,
    limit,
    nextCursor,
    pageIndex,
  });
}

export function emptyCampaignListPage(pageIndex = 0) {
  return campaignListPagePresentation([], { pageIndex });
}

export function campaignListRangeLabel(status, page) {
  if (status === 'loading') return `Loading ${page?.pageLabel?.toLowerCase() || 'campaigns'}`;
  if (status === 'error') return `${page?.pageLabel || 'Campaign page'} unavailable`;
  return page?.rangeLabel || '0 campaigns';
}

export function campaignEmptyState(archiveView, page) {
  if (page?.hasPrevious) {
    return Object.freeze({
      title: 'No campaigns on this page',
      body: 'This page no longer has campaigns. Return to the previous page to continue.',
    });
  }
  if (archiveView === 'archived') {
    return Object.freeze({
      title: 'No archived campaigns',
      body: 'Campaigns archived in BeyondLeads will appear here with their saved settings and historical performance.',
    });
  }
  if (archiveView === 'all') {
    return Object.freeze({
      title: 'No campaigns in this company',
      body: 'Create a campaign from Home to begin building this company’s campaign history.',
    });
  }
  return Object.freeze({
    title: 'No current campaigns',
    body: 'Create a campaign from Home, or switch to Archived to review preserved campaign history.',
  });
}

export function CampaignListPagination({ page, onPageChange }) {
  if (!page || (!page.count && !page.hasPrevious)) return null;
  return (
    <nav className="bl-campaign-pagination" aria-label="Campaign list pages">
      <span>{page.pageLabel}</span>
      <div>
        <button
          type="button"
          aria-label="Previous campaign page"
          disabled={!page.hasPrevious}
          onClick={() => onPageChange?.(page.pageIndex - 1)}
        >Previous</button>
        <button
          type="button"
          aria-label="Next campaign page"
          disabled={!page.hasNext}
          onClick={() => onPageChange?.(page.pageIndex + 1)}
        >Next</button>
      </div>
    </nav>
  );
}

export function CampaignSummaryBatchStatus({
  batch = null,
  hasPreservedData = false,
  onRetry,
  rangeLabel,
  refreshExhausted = false,
  status,
}) {
  if (status === 'loading') {
    return (
      <div className="bl-campaign-summary-batch-status">
        <Beacon size="control" tone="neutral" label={`Checking Meta performance for ${rangeLabel}…`} delayMs={0} />
      </div>
    );
  }
  // OGC-478: some rows are waiting on a Meta resource refresh. The list
  // re-reads on its own a bounded number of times; once those re-reads are
  // used up it asks for a manual refresh instead of promising recovery.
  if (status === 'ready' && batch?.availability === 'partial' && summaryBatchIsRefreshing(batch)) {
    return (
      <div className="bl-campaign-summary-batch-status" role="status">
        {refreshExhausted
          ? 'Some campaigns are still waiting on a Meta access refresh. Refresh the page to check again, or ask a company owner or admin to open them; saved campaign settings are unchanged.'
          : 'Refreshing Meta access for some campaigns. This list re-checks shortly; saved campaign settings are unchanged.'}
      </div>
    );
  }
  // OGC-488: some rows were published with a Meta login Meta can no
  // longer confirm. Not temporary, so no re-check is promised.
  if (status === 'ready' && batch?.availability === 'partial' && summaryBatchHasUnprovableOriginal(batch)) {
    return (
      <div className="bl-campaign-summary-batch-status" role="status">
        Some campaigns were published with a Meta login that Meta can no longer confirm, so their status and spend can't be shown here. Open one for options; nothing in Meta was changed.
      </div>
    );
  }
  if (status === 'ready' && batch?.availability === 'partial') return null;
  if (status !== 'error') return null;
  return (
    <div className="bl-campaign-summary-batch-status is-error" role="alert">
      <span>{hasPreservedData
        ? 'Performance refresh failed. Showing the last loaded performance; saved campaign settings are unchanged.'
        : 'Performance is temporarily unavailable. Rows show N/A and saved campaign settings are unchanged.'}</span>
      {onRetry && <button type="button" onClick={onRetry}>Retry performance</button>}
    </div>
  );
}

export function campaignManagementConfirmation(action) {
  if (action === 'archive') {
    return 'Archive this campaign? Published delivery will be paused and verified with Meta before it moves to Archived. Drafts archive directly. History and individual ad settings stay saved.';
  }
  if (action === 'restore') {
    return 'Restore this campaign? It returns to the current view without resuming delivery. BeyondLeads must verify current Meta state before an explicit resume.';
  }
  if (action === 'delete') {
    return 'This deletes the campaign from Meta and from BeyondLeads. It cannot be undone.';
  }
  if (action === 'delete_draft') {
    return 'This deletes the draft from BeyondLeads. It cannot be undone. Nothing was created on Meta.';
  }
  return '';
}

function count(value) {
  return Number.isSafeInteger(value) && value >= 0 ? value : null;
}

function plural(value, singular, pluralForm = `${singular}s`) {
  return `${value} ${value === 1 ? singular : pluralForm}`;
}

// OGC-452: the ledger the delete dialog shows. Counts come from the sealed
// publish evidence and the Details payload; an unknown count reads "—".
export function campaignDeleteLedger(detail, management) {
  const mode = management?.deleteEligibility?.mode === 'tombstone' ? 'tombstone' : 'hard';
  if (mode !== 'tombstone') {
    return [
      { label: 'Deleted from BeyondLeads', value: 'This draft and its saved settings' },
      { label: 'Meta', value: 'Nothing was created on Meta' },
    ];
  }
  const expected = detail?.provider?.hierarchy?.expected || {};
  const adCount = count(expected.adCount) ?? count(detail?.configuration?.ads?.length);
  const adSetCount = count(expected.adSetCount) ?? (adCount === null ? null : 1);
  const objects = adCount === null
    ? 'the campaign, its ad set and its ads'
    : `1 campaign, ${plural(adSetCount ?? 1, 'ad set')}, ${plural(adCount, 'ad')}`;
  const leads = webhookLeadMetric(detail);
  const leadCount = leads.availability === 'available' ? count(leads.value) : null;
  return [
    { label: 'Deleted from Meta', value: objects },
    { label: 'Spend and results', value: 'Kept in reports' },
    { label: 'Leads', value: leadCount === null ? 'Kept, labelled “Deleted campaign”' : `${leadCount} — kept, labelled “Deleted campaign”` },
    { label: 'Instant Form', value: 'Stays on your Facebook Page' },
  ];
}

export function campaignDeleteDialogOptions(detail, management) {
  const name = exactText(detail?.campaign?.name);
  const published = management?.deleteEligibility?.mode === 'tombstone';
  return {
    eyebrow: 'Delete campaign',
    title: name ? `Delete “${name}”?` : 'Delete this campaign?',
    confirmLabel: 'Delete campaign',
    tone: 'danger',
    description: confirmationConsequence(campaignManagementConfirmation(published ? 'delete' : 'delete_draft')),
    details: campaignDeleteLedger(detail, management),
    confirmation: { label: 'Type the campaign name to confirm', expected: name,
      hint: 'Enter the campaign name exactly as shown' },
  };
}

function campaignManagementDialogOptions(action) {
  const presentation = {
    archive: {
      title: 'Archive campaign?',
      confirmLabel: 'Archive campaign',
      tone: 'warning',
    },
    restore: {
      title: 'Restore campaign?',
      confirmLabel: 'Restore campaign',
      tone: 'accent',
    },
    delete: {
      title: 'Delete this campaign?',
      confirmLabel: 'Delete campaign',
      tone: 'danger',
    },
  }[action];
  const confirmation = campaignManagementConfirmation(action);
  if (!presentation || !confirmation) return null;
  return {
    eyebrow: 'BeyondLeads management',
    ...presentation,
    description: confirmationConsequence(confirmation),
  };
}

export function campaignManagementMutationConfirmed(action, management) {
  if (action === 'archive') return typeof management?.archivedAt === 'string';
  if (action === 'restore') {
    return management?.archivedAt === null
      && ['verified', 'not_required'].includes(management?.restoreVerification?.status);
  }
  return false;
}

export function campaignManagementCommandSettled(action, management) {
  if (action === 'archive') return typeof management?.archivedAt === 'string';
  if (action === 'restore') return management?.archivedAt === null;
  return false;
}

export function campaignRestoreRequiresCanonicalDetail(management) {
  return management?.archivedAt === null
    && ['required', 'failed'].includes(management?.restoreVerification?.status);
}

export function campaignDetailManagementRefreshPresentation(action, {
  management,
  refreshComplete = false,
} = {}) {
  const confirmed = campaignManagementMutationConfirmed(action, management);
  const settled = campaignManagementCommandSettled(action, management);
  if (confirmed) {
    return Object.freeze({
      confirmed,
      settled,
      status: refreshComplete ? 'success' : 'warning',
      // OGC-455: an archive that never reached Meta confirmed nothing there.
      // Saying "delivery is confirmed paused" would contradict the
      // confirmation the owner just accepted, and claim a verification that
      // did not happen — the one thing this tier must never do.
      message: action === 'archive'
        ? management?.archiveEvidence?.tier === 'unobservable'
          ? refreshComplete
            ? 'Campaign archived in BeyondLeads. Nothing in Meta was changed — if this campaign is still running there, it keeps running and spending until you pause it in Meta Ads Manager. Leads, individual ad settings and history are preserved.'
            : 'Campaign archived in BeyondLeads, but the latest detail could not be fully refreshed. Nothing in Meta was changed — if this campaign is still running there, it keeps running and spending until you pause it in Meta Ads Manager. Leads, individual ad settings and history are preserved.'
          : refreshComplete
            ? 'Campaign archived in BeyondLeads. Durable detail and management controls were refreshed; Published Meta delivery is confirmed paused; individual ad settings and history are preserved.'
            : 'Campaign archived in BeyondLeads, but the latest detail could not be fully refreshed. Published Meta delivery is confirmed paused; individual ad settings and history are preserved.'
        : refreshComplete
          ? 'Campaign restored. Durable detail and management controls were refreshed.'
          : 'Campaign restored, but its latest detail could not be fully refreshed.',
    });
  }
  if (action === 'restore' && settled && campaignRestoreRequiresCanonicalDetail(management)) {
    return Object.freeze({
      confirmed,
      settled,
      status: 'warning',
      message: 'Campaign restored in BeyondLeads. It remains read-only while this Detail view verifies its current Meta state.',
    });
  }
  return Object.freeze({
    confirmed,
    settled,
    status: 'warning',
    message: `${action === 'restore' ? 'Restore' : 'Archive'} is not yet confirmed. Refresh status before retrying.`,
  });
}

export function isCurrentCampaignManagementMutation({
  currentListScope,
  currentScope,
  expectedListScope,
  expectedScope,
  currentVersion,
  version,
} = {}) {
  return Boolean(expectedScope)
    && currentScope === expectedScope
    && (!expectedListScope || currentListScope === expectedListScope)
    && currentVersion === version;
}

export function isCurrentCampaignDetailManagementMutation({
  mounted,
  currentScope,
  expectedScope,
  currentVersion,
  version,
} = {}) {
  return mounted === true
    && Boolean(expectedScope)
    && currentScope === expectedScope
    && currentVersion === version;
}

export function canStartCampaignDetailManagementMutation({
  action,
  detailBusy = false,
  management,
  managementBusy = false,
  productPolicy,
} = {}) {
  return CAMPAIGN_MANAGEMENT_ACTIONS.has(action)
    && !detailBusy
    && !managementBusy
    && management?.actions?.[action]?.allowed === true
    && hasNamedTenantAction(productPolicy, `campaign.${action}`)
    && (action !== 'delete' || management?.deleteEligibility?.eligible === true);
}

export function campaignRowBudgetConfirmed(summary, monthlyMajor) {
  return typeof monthlyMajor === 'string'
    && summary?.settings?.monthlyBudget?.monthlyMajor === monthlyMajor;
}

function retainCampaignManagementRetryKey(error) {
  return !Number.isSafeInteger(error?.status)
    || !DEFINITIVE_MANAGEMENT_REJECTION_STATUSES.has(error.status);
}

export function campaignResumeLabel(status) {
  if (status === 'draft') return 'Resume setup';
  if (status === 'awaiting_approval') return 'Review campaign';
  if (status === 'approved') return 'View campaign review';
  return 'View campaign';
}

export function campaignDestination(campaign) {
  if (
    !campaign?.id
    || typeof campaign.id !== 'string'
    || campaign.id.trim() !== campaign.id
  ) return null;
  const encodedId = encodeURIComponent(campaign.id);
  if (campaign.status === 'draft') return `curated-campaign/${encodedId}`;
  if (['awaiting_approval', 'approved'].includes(campaign.status)) {
    return `curated-campaign/${encodedId}?step=review`;
  }
  if (CAMPAIGN_DETAIL_STATES.has(campaign.status)) return `campaigns/${encodedId}`;
  return null;
}

export function campaignCommandScopeKey(companyId, campaignId) {
  return JSON.stringify([String(companyId || ''), String(campaignId || '')]);
}

export function statusLabel(status) {
  const labels = {
    draft: 'Draft',
    awaiting_approval: 'Ready to review',
    approved: 'Approved',
    publishing: 'Publishing',
    paused: 'Paused',
    active: 'Active',
    partial: 'Partial',
    failed: 'Failed',
    completed: 'Completed',
    archived: 'Archived',
  };
  return labels[status] || 'Unavailable';
}

// OGC-405: `curated_campaign_publish_binding_unavailable` is the expected,
// transient shape of the published Meta campaign mapping while a campaign is
// still `publishing` and its creation progress is actively `running` — the
// mapping has simply not been written yet. Surfacing it as a red "needs
// attention" notice during normal in-progress creation is a false alarm, so
// this failure code is suppressed for the attention notice specifically in
// that window. It is not suppressed once the campaign leaves `publishing`
// (paused/active/partial/failed), where the same code means the mapping is
// genuinely missing and the notice must stay.
const CURATED_PUBLISH_BINDING_PENDING_CODE = 'curated_campaign_publish_binding_unavailable';

export function curatedCampaignAttentionFailureCode(failureCode, campaignStatus, publishProgressState) {
  const code = exactText(failureCode);
  const isPublishingWithRunningProgress = exactText(campaignStatus).toLowerCase() === 'publishing'
    && exactText(publishProgressState).toLowerCase() === 'running';
  if (code === CURATED_PUBLISH_BINDING_PENDING_CODE && isPublishingWithRunningProgress) return '';
  return code;
}

// OGC-408: while a campaign is `publishing` and its creation progress is
// actively `running`, Meta insights/hierarchy reads are expected to be
// partial/stale/unavailable mid-creation — nothing has spent yet and the
// mapping may not be written. Surfacing that as the red "Campaign needs
// attention" freshness banner during normal in-progress creation is a false
// alarm, mirroring the OGC-405 mapping-unavailable suppression above. This
// only suppresses the metrics-freshness attention driver, and only in that
// exact window; a paused/active/partial/failed campaign with genuinely
// stale metrics still shows the banner, and every other attention driver
// (failure codes, hierarchy attention, reconciliation status) is unchanged.
//
// OGC-408 follow-up: the same `publishing`+`running` window makes
// `providerAvailability`, `metricsAvailability`, and `reconciliationStatus`
// read as partial/stale/unavailable (and pending, for reconciliation) too —
// the provider binding simply is not sealed yet, so nothing downstream of it
// can be verified. Each of those drivers needs the identical window check,
// so it lives once here rather than as a fourth duplicated predicate; every
// per-driver suppression below is a one-line delegation to it with its own
// set of suppressed states.
export function curatedCampaignPublishingWindowAttentionState(state, campaignStatus, publishProgressState, suppressedStates) {
  const normalized = exactText(state).toLowerCase();
  const isPublishingWithRunningProgress = exactText(campaignStatus).toLowerCase() === 'publishing'
    && exactText(publishProgressState).toLowerCase() === 'running';
  if (isPublishingWithRunningProgress && suppressedStates.has(normalized)) return '';
  return normalized;
}

const STALE_METRICS_FRESHNESS_STATES = new Set(['partial', 'stale', 'unavailable']);
export function curatedCampaignAttentionMetricsFreshness(freshness, campaignStatus, publishProgressState) {
  return curatedCampaignPublishingWindowAttentionState(freshness, campaignStatus, publishProgressState, STALE_METRICS_FRESHNESS_STATES);
}

const STALE_PROVIDER_AVAILABILITY_STATES = new Set(['partial', 'stale', 'unavailable']);
export function curatedCampaignAttentionProviderAvailability(providerAvailability, campaignStatus, publishProgressState) {
  return curatedCampaignPublishingWindowAttentionState(providerAvailability, campaignStatus, publishProgressState, STALE_PROVIDER_AVAILABILITY_STATES);
}

const STALE_METRICS_AVAILABILITY_STATES = new Set(['partial', 'stale', 'unavailable']);
export function curatedCampaignAttentionMetricsAvailability(metricsAvailability, campaignStatus, publishProgressState) {
  return curatedCampaignPublishingWindowAttentionState(metricsAvailability, campaignStatus, publishProgressState, STALE_METRICS_AVAILABILITY_STATES);
}

// Unlike the other drivers, a `failed` or `partial` reconciliation status is
// never suppressed here, even mid-creation: those states mean Meta actively
// rejected or only partially applied something, not merely "not sealed yet",
// and the banner must keep firing for them. Only pending/stale/unavailable —
// the "nothing to reconcile against yet" states — are gated in the window.
const PENDING_RECONCILIATION_STATES = new Set(['pending', 'stale', 'unavailable']);
export function curatedCampaignAttentionReconciliationStatus(reconciliationStatus, campaignStatus, publishProgressState) {
  return curatedCampaignPublishingWindowAttentionState(reconciliationStatus, campaignStatus, publishProgressState, PENDING_RECONCILIATION_STATES);
}

export function campaignAttentionPresentation({
  fallbackMessage,
  failureCode,
  reconciliationStatus,
} = {}) {
  const code = exactText(failureCode);
  const reconciliation = exactText(reconciliationStatus).toLowerCase();

  if (code) {
    const knownPresentation = CAMPAIGN_ATTENTION_PRESENTATIONS.get(code);
    if (knownPresentation) return knownPresentation;
    return {
      title: reconciliation === 'pending' ? 'Meta confirmation is pending' : 'Campaign needs attention',
      body: safeFailureMessage({ code }, 'curated-campaign'),
    };
  }
  return {
    title: reconciliation === 'pending' ? 'Meta confirmation is pending' : 'Campaign needs attention',
    body: exactText(
      fallbackMessage,
      'The durable campaign status records an incomplete provider operation. Use an available control to retry safely.',
    ),
  };
}

export function unwrapCampaignDetail(value) {
  const candidate = value?.detail && typeof value.detail === 'object' ? value.detail : value;
  return candidate && typeof candidate === 'object' && candidate.campaign?.id ? candidate : null;
}

export function validatedCampaignDetail(response, campaignId) {
  const detail = unwrapCampaignDetail(response);
  if (!detail || detail.campaign.id !== campaignId) {
    const error = new Error('Campaign detail response is invalid.');
    error.code = 'curated_campaign_detail_invalid';
    throw error;
  }
  const configurationSource = detail.configuration
    && typeof detail.configuration === 'object'
    && !Array.isArray(detail.configuration)
    ? detail.configuration
    : {};
  const hasNestedTarget = Object.hasOwn(configurationSource, 'metaTarget');
  const nestedTarget = hasNestedTarget ? configurationSource.metaTarget : undefined;
  const hasTarget = Object.hasOwn(detail, 'metaTarget') || hasNestedTarget;
  if (!hasTarget) return detail;
  const rawTarget = Object.hasOwn(detail, 'metaTarget') ? detail.metaTarget : nestedTarget;
  const metaTarget = rawTarget == null
    ? null
    : normalizeMetaCampaignTargetPresentation(rawTarget);
  if (rawTarget != null && !metaTarget) {
    const error = new Error('Campaign detail response is invalid.');
    error.code = 'curated_campaign_detail_invalid';
    throw error;
  }
  const configuration = Object.fromEntries(
    Object.entries(configurationSource).filter(([key]) => key !== 'metaTarget'),
  );
  return Object.freeze({
    ...detail,
    configuration,
    metaTarget,
  });
}

export function metricValue(metric) {
  if (metric && typeof metric === 'object' && !Array.isArray(metric)) {
    const isNumeric = typeof metric.value === 'number'
      || (typeof metric.value === 'string' && metric.value.trim() !== '');
    return {
      value: isNumeric && Number.isFinite(Number(metric.value))
        ? Number(metric.value)
        : null,
      availability: exactText(
        metric.availability,
        isNumeric && Number.isFinite(Number(metric.value)) ? 'available' : 'unavailable',
      ),
      source: exactText(metric.source, 'meta_insights'),
      unit: exactText(metric.unit),
    };
  }
  const isNumeric = typeof metric === 'number'
    || (typeof metric === 'string' && metric.trim() !== '');
  return {
    value: isNumeric && Number.isFinite(Number(metric)) ? Number(metric) : null,
    availability: isNumeric && Number.isFinite(Number(metric)) ? 'available' : 'unavailable',
    source: 'meta_insights',
    unit: '',
  };
}

function metricMap(detail) {
  const envelope = detail?.metrics || {};
  return envelope.metrics || envelope.values || envelope;
}

function leadMeasures(detail) {
  return detail?.metrics?.leadMeasures || detail?.leadMeasures || {};
}

function webhookLeadMetric(detail) {
  const leadEnvelope = detail?.leads || {};
  const candidate = leadEnvelope.webhookIngested
    || leadEnvelope.webhook
    || leadMeasures(detail).webhookIngested
    || (Object.hasOwn(leadEnvelope, 'webhookCount')
      ? { value: leadEnvelope.webhookCount, availability: leadEnvelope.availability, source: 'webhook_ingested_lead_rows' }
      : null);
  return metricValue(candidate);
}

function metaLeadMetric(detail) {
  const metrics = metricMap(detail);
  return metricValue(
    metrics.metaReportedLeads
    ?? metrics.metaLeads
    ?? detail?.leads?.metaReported
    ?? leadMeasures(detail).metaReported,
  );
}

function controlsFor(detail) {
  const controls = detail?.controls || {};
  const allowedActions = Array.isArray(controls.allowedActions) ? controls.allowedActions : [];
  return Object.fromEntries(Object.keys(ACTIONS).map((action) => {
    const candidate = controls[action];
    const namedCandidate = controls[
      action === 'budget'
        ? 'canUpdateBudget'
        : `can${action[0].toUpperCase()}${action.slice(1)}`
    ];
    const allowed = candidate === true
      || candidate?.allowed === true
      || candidate?.enabled === true
      || namedCandidate === true
      || allowedActions.includes(action)
      || allowedActions.includes(ACTIONS[action].productAction);
    return [action, allowed];
  }));
}

export function campaignControlsForPolicy(detail, productPolicy) {
  const serverControls = controlsFor(detail);
  return Object.fromEntries(Object.entries(ACTIONS).map(([action, definition]) => [
    action,
    Boolean(serverControls[action] && hasNamedTenantAction(productPolicy, definition.productAction)),
  ]));
}

export function canRetryCuratedMetaPublish(detail, productPolicy) {
  const workflowJobId = exactText(detail?.configuration?.approval?.workflowJobId);
  return hasNamedTenantAction(productPolicy, 'campaign.create_paused')
    && curatedMetaPublishRecovery(detail?.campaign?.status, workflowJobId).canRetry
    && Boolean(curatedMetaPublishConfirmation(
      curatedMetaPublishSnapshotFromDetail(detail),
      { retry: true },
    ));
}

function dateTime(value) {
  if (!value) return 'Unavailable';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return 'Unavailable';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(parsed);
}

function number(value, options = {}) {
  return new Intl.NumberFormat(undefined, options).format(value);
}

export function formatCampaignMetric(metric, kind, currency = '') {
  const normalized = metricValue(metric);
  if (normalized.availability === 'not_applicable') return 'Not applicable';
  if (normalized.value === null) return normalized.availability === 'partial' ? 'Partial' : 'Unavailable';
  if (kind === 'money') {
    const safeCurrency = /^[A-Z]{3}$/.test(currency) ? currency : '';
    return safeCurrency
      ? number(normalized.value, { style: 'currency', currency: safeCurrency, maximumFractionDigits: 2 })
      : number(normalized.value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (kind === 'percent') return `${number(normalized.value, { maximumFractionDigits: 2 })}%`;
  if (kind === 'frequency') return number(normalized.value, { maximumFractionDigits: 2 });
  return number(normalized.value, { maximumFractionDigits: 0 });
}

export function operationRetryKey(action, campaignId, createId = () => globalThis.crypto?.randomUUID?.()) {
  const safeSegment = (value, fallback, maximumLength) => {
    const normalized = exactText(value, fallback).replace(/[^A-Za-z0-9:_-]/g, '_');
    return (normalized || fallback).slice(0, maximumLength);
  };
  const nonce = exactText(createId()) || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return [
    'curated-campaign',
    safeSegment(action, 'operation', 48),
    safeSegment(campaignId, 'campaign', 80),
    safeSegment(nonce, 'request', 48),
  ].join(':');
}

export function canonicalMonthlyMajor(value) {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (text.length < 1 || text.length > 32 || !/^\d+(?:\.\d{1,2})?$/.test(text)) return null;
  const [wholePart, fractionPart = ''] = text.split('.');
  try {
    const whole = BigInt(wholePart);
    const minor = (whole * 100n) + BigInt(fractionPart.padEnd(2, '0'));
    if (minor < 1n || minor > BigInt(Number.MAX_SAFE_INTEGER)) return null;
    return `${whole.toString()}.${fractionPart.padEnd(2, '0')}`;
  } catch {
    return null;
  }
}

export function scopedOperationRetryKey(cache, {
  companyId,
  campaignId,
  action,
  monthlyMajor = '',
  createId,
} = {}) {
  const canonicalBudget = action === 'budget' ? canonicalMonthlyMajor(monthlyMajor) : null;
  const requestIdentity = action === 'budget'
    ? `${action}:${canonicalBudget || monthlyMajor}`
    : action;
  const cacheIdentity = `${companyId}:${campaignId}:${requestIdentity}`;
  let idempotencyKey = cache.get(cacheIdentity);
  if (!idempotencyKey) {
    idempotencyKey = operationRetryKey(
      requestIdentity,
      `${companyId}:${campaignId}`,
      createId,
    );
    cache.set(cacheIdentity, idempotencyKey);
  }
  return { cacheIdentity, idempotencyKey };
}

export function mutationBody(action, idempotencyKey, monthlyMajor = '') {
  const canonicalBudget = action === 'budget' ? canonicalMonthlyMajor(monthlyMajor) : null;
  return {
    idempotencyKey,
    ...(action === 'budget' ? { budget: { monthlyMajor: canonicalBudget || '' } } : {}),
  };
}

export function campaignBudgetStateAfterDetail(current, {
  scope,
  detail,
  acceptDurable = false,
  expectedRequestedValue = '',
  retryMonthlyMajor = '',
} = {}) {
  const durableValue = detail?.configuration?.budget?.monthlyMajor
    ?? detail?.campaign?.budget?.monthlyMajor
    ?? detail?.provider?.budget?.monthlyMajor;
  const canonicalDurable = canonicalMonthlyMajor(String(durableValue ?? '')) || '';
  if (!acceptDurable && current?.scope === scope && current.preserve === true) return current;
  const canonicalRetry = canonicalMonthlyMajor(retryMonthlyMajor);
  if (!acceptDurable && canonicalRetry) {
    return { scope, value: canonicalRetry, preserve: true };
  }
  const canonicalExpected = canonicalMonthlyMajor(expectedRequestedValue);
  const canonicalCurrent = canonicalMonthlyMajor(current?.value);
  if (
    acceptDurable
    && canonicalExpected
    && current?.scope === scope
    && current.preserve === true
    && canonicalCurrent !== canonicalExpected
  ) {
    return current;
  }
  return { scope, value: canonicalDurable, preserve: false };
}

function clearOperationRetryScope(retryKeys, trackedRequests, budgetScope) {
  if (!budgetScope) return;
  if (trackedRequests instanceof Map) {
    for (const [cacheIdentity, tracked] of trackedRequests) {
      if (tracked?.budgetScope === budgetScope) {
        trackedRequests.delete(cacheIdentity);
        if (retryKeys instanceof Map) retryKeys.delete(cacheIdentity);
      }
    }
  }
  if (retryKeys instanceof Map) {
    const scopePrefix = `${budgetScope}:`;
    for (const cacheIdentity of retryKeys.keys()) {
      if (cacheIdentity.startsWith(scopePrefix)) retryKeys.delete(cacheIdentity);
    }
  }
}

export function hydrateRetryStateFromDetail(retryKeys, trackedRequests, {
  companyId,
  campaignId,
  detail,
} = {}) {
  if (!(retryKeys instanceof Map) || !(trackedRequests instanceof Map)) return null;
  const expectedCompanyId = exactText(companyId);
  const expectedCampaignId = exactText(campaignId);
  const budgetScope = expectedCompanyId && expectedCampaignId
    ? `${expectedCompanyId}:${expectedCampaignId}`
    : '';
  const failClosed = () => {
    clearOperationRetryScope(retryKeys, trackedRequests, budgetScope);
    return null;
  };
  const campaign = detail?.campaign;
  const operation = detail?.lastOperation;
  if (
    !expectedCompanyId
    || !expectedCampaignId
    || exactText(campaign?.id) !== expectedCampaignId
  ) return null;
  if (!operation) return null;

  const mismatchedScope = [
    [detail?.companyId, expectedCompanyId],
    [campaign?.companyId, expectedCompanyId],
    [operation?.companyId, expectedCompanyId],
    [operation?.campaignId, expectedCampaignId],
  ].some(([actual, expected]) => (
    actual !== undefined
    && actual !== null
    && exactText(actual) !== expected
  ));
  if (mismatchedScope) return failClosed();

  const operationId = exactText(operation.id);
  const action = OPERATION_ACTIONS[exactText(operation.type)];
  const status = exactText(operation.status).toLowerCase();
  const reconciliationStatus = exactText(operation.reconciliationStatus).toLowerCase();
  const isPending = status === 'accepted' && reconciliationStatus === 'pending';
  const isFailed = status === 'failed' && reconciliationStatus === 'failed';
  const descriptor = operation.retryDescriptor;
  if (status === 'completed' && reconciliationStatus === 'confirmed') return null;
  if (
    !operationId
    || !action
    || (!isPending && !isFailed)
    || !descriptor
    || typeof descriptor !== 'object'
    || Array.isArray(descriptor)
  ) return failClosed();

  const descriptorKeys = Object.keys(descriptor);
  const token = exactText(descriptor.token);
  const expectedDescriptorKeys = action === 'budget'
    ? ['monthlyMajor', 'token']
    : ['token'];
  if (
    descriptorKeys.length !== expectedDescriptorKeys.length
    || !expectedDescriptorKeys.every((key) => Object.hasOwn(descriptor, key))
    || token !== descriptor.token
    || !RETRY_TOKEN_PATTERN.test(token)
  ) return failClosed();

  const monthlyMajor = action === 'budget'
    ? canonicalMonthlyMajor(descriptor.monthlyMajor)
    : '';
  if (
    (action === 'budget' && (!monthlyMajor || monthlyMajor !== descriptor.monthlyMajor))
    || (action !== 'budget' && Object.hasOwn(descriptor, 'monthlyMajor'))
  ) return failClosed();

  const requestIdentity = action === 'budget' ? `${action}:${monthlyMajor}` : action;
  const cacheIdentity = `${budgetScope}:${requestIdentity}`;
  for (const [candidateIdentity, tracked] of trackedRequests) {
    if (
      candidateIdentity !== cacheIdentity
      && tracked?.budgetScope === budgetScope
      && tracked?.action === action
    ) {
      trackedRequests.delete(candidateIdentity);
      retryKeys.delete(candidateIdentity);
    }
  }
  const tracked = {
    action,
    budgetScope,
    monthlyMajor,
    operationId,
    verb: ACTIONS[action].verb,
  };
  retryKeys.set(cacheIdentity, token);
  trackedRequests.set(cacheIdentity, tracked);
  return { cacheIdentity, idempotencyKey: token, tracked };
}

export function mutationOperationId(response) {
  const operation = response?.operation
    || response?.detail?.lastOperation
    || response?.lastOperation;
  return exactText(operation?.id);
}

export function matchingTrackedOperation(trackedRequests, {
  budgetScope,
  detail,
  requestedCacheIdentity = '',
} = {}) {
  if (!(trackedRequests instanceof Map)) return null;
  const lastOperationId = exactText(detail?.lastOperation?.id);
  if (!lastOperationId) return null;
  const entries = [...trackedRequests.entries()].filter(([, tracked]) => (
    tracked?.budgetScope === budgetScope
  ));
  const selected = requestedCacheIdentity
    ? entries.find(([cacheIdentity, tracked]) => (
      cacheIdentity === requestedCacheIdentity
      && tracked.operationId === lastOperationId
    ))
    : entries.find(([, tracked]) => tracked.operationId === lastOperationId);
  if (!selected) return null;
  return { cacheIdentity: selected[0], tracked: selected[1] };
}

export function trackedOperationForDetailOrClearScope(retryKeys, trackedRequests, options = {}) {
  const selected = matchingTrackedOperation(trackedRequests, options);
  const budgetScope = exactText(options.budgetScope);
  if (selected) {
    if (trackedRequests instanceof Map) {
      for (const [cacheIdentity, tracked] of trackedRequests) {
        if (cacheIdentity !== selected.cacheIdentity && tracked?.budgetScope === budgetScope) {
          trackedRequests.delete(cacheIdentity);
          if (retryKeys instanceof Map) retryKeys.delete(cacheIdentity);
        }
      }
    }
    if (retryKeys instanceof Map) {
      const scopePrefix = `${budgetScope}:`;
      for (const cacheIdentity of retryKeys.keys()) {
        if (cacheIdentity !== selected.cacheIdentity && cacheIdentity.startsWith(scopePrefix)) {
          retryKeys.delete(cacheIdentity);
        }
      }
    }
    return selected;
  }
  clearOperationRetryScope(retryKeys, trackedRequests, budgetScope);
  return null;
}

// OGC-404: a detail reconcile that finds no matching tracked operation must
// only reset a mutation belonging to the tracked lifecycle actions (the
// values of OPERATION_ACTIONS: activate/pause/resume/budget) — those are the
// only actions ever recorded in `trackedRequests`/`retryKeys`, so "no match"
// genuinely means "this one is stale". A `publish` mutation (create/resume/
// retry) is never tracked there; it is owned and settled entirely by
// `retryMetaPublish` itself. Clearing it here raced a pending Resume click
// against a `failed` progress read the server was still overwriting,
// defeating the OGC-404 polling fix in `curatedCampaignPublishProgressPollingOptions`
// (a `failed` read flipped the mutation to idle, which flipped
// `continueOnFailed` back to false, which stopped the poll timer on the very
// checkpoint it was meant to survive). Any other non-tracked action (for
// example `recover_meta_access`, which settles itself the same way) is
// preserved for the same reason. Factored out so the decision is
// unit-testable without rendering the hook.
const TRACKED_MUTATION_ACTIONS = new Set(Object.values(OPERATION_ACTIONS));
export function mutationClearedByDetailReconcile(mutation, selectedOperation) {
  if (selectedOperation) return true;
  const action = exactText(mutation?.action);
  if (!action) return false;
  return TRACKED_MUTATION_ACTIONS.has(action);
}

export function durableMutationReconciliation(detail, action, expectedOperationId = '') {
  const lastOperation = detail?.lastOperation;
  const expectedType = {
    activate: 'campaign_activate',
    pause: 'campaign_pause',
    resume: 'campaign_resume',
    budget: 'campaign_budget_update',
  }[action];
  const actualType = lastOperation?.type || lastOperation?.kind || lastOperation?.operationType;
  const normalizedType = actualType === action ? expectedType : actualType;
  const actualOperationId = exactText(lastOperation?.id);
  const requiredOperationId = exactText(expectedOperationId);
  const status = exactText(lastOperation?.status).toLowerCase();
  const reconciliationStatus = exactText(
    lastOperation?.reconciliationStatus,
    exactText(detail?.provider?.reconciliationStatus),
  ).toLowerCase();
  const failureCode = lastOperation?.failureCode
    || lastOperation?.errorCode
    || detail?.provider?.error?.code
    || (typeof detail?.provider?.error === 'string' ? detail.provider.error : '')
    || detail?.provider?.errorCode
    || '';

  if (
    !lastOperation
    || !expectedType
    || normalizedType !== expectedType
    || (requiredOperationId && actualOperationId !== requiredOperationId)
  ) {
    return { outcome: 'pending', clearRetryKey: false, failureCode: '' };
  }
  if (status === 'failed' || reconciliationStatus === 'failed') {
    return { outcome: 'failed', clearRetryKey: false, failureCode };
  }
  if (
    reconciliationStatus === 'confirmed'
    && ['completed', 'succeeded'].includes(status)
  ) {
    return { outcome: 'confirmed', clearRetryKey: true, failureCode: '' };
  }
  return { outcome: 'pending', clearRetryKey: false, failureCode };
}

export function durableMutationPresentation(result, verb) {
  if (result?.outcome === 'confirmed' && result.clearRetryKey === true) {
    return {
      status: 'success',
      clearRetryKey: true,
      message: `${verb} is confirmed in the latest campaign status.`,
    };
  }
  if (result?.outcome === 'failed') {
    const failure = safeFailureMessage({ code: result.failureCode }, 'curated-campaign');
    return {
      status: 'error',
      clearRetryKey: false,
      message: `${failure} Retrying this action will reuse the same request.`,
    };
  }
  return {
    status: 'warning',
    clearRetryKey: false,
    message: 'Meta confirmation is still pending. Refresh before retrying; a retry will reuse the same request.',
  };
}

export default function CuratedCampaigns({ session, navigate, campaignId = '', canCreate = true }) {
  const companyId = session?.user?.activeCompany?.id || session?.user?.tenant?.companyId || '';
  if (campaignId) {
    return <CuratedCampaignDetail
      key={campaignCommandScopeKey(companyId, campaignId)}
      campaignId={campaignId}
      companyId={companyId}
      navigate={navigate}
      productPolicy={session?.user?.productPolicy}
    />;
  }
  return <CuratedCampaignList
    canCreate={canCreate}
    companyId={companyId}
    navigate={navigate}
    productPolicy={session?.user?.productPolicy}
  />;
}

// OGC-452: Details hands the list a one-shot notice when a campaign is gone.
const pendingCampaignListNotices = new Map();

export function queueCampaignListNotice(companyId, notice) {
  const normalizedCompanyId = exactText(companyId);
  if (!normalizedCompanyId || !notice?.message) return;
  pendingCampaignListNotices.set(normalizedCompanyId, notice);
}

export function takeCampaignListNotice(companyId) {
  const normalizedCompanyId = exactText(companyId);
  const notice = pendingCampaignListNotices.get(normalizedCompanyId) || null;
  pendingCampaignListNotices.delete(normalizedCompanyId);
  return notice;
}

export function CuratedCampaignList(props) {
  const [initialFeedback] = useState(() => takeCampaignListNotice(props.companyId));
  return <CampaignInventory {...props} initialFeedback={initialFeedback} />;
}

export function CuratedCampaignDetail({ campaignId, companyId, navigate, productPolicy }) {
  const billing = useCompanyBilling();
  const [datePreset, setDatePreset] = useState('last_30d');
  const [detailState, setDetailState] = useState({ scope: '', status: 'loading', detail: null, error: null });
  const [mutation, setMutation] = useState({ scope: '', action: '', status: 'idle', message: '' });
  const [detailManagementState, setDetailManagementState] = useState({ scope: '', status: 'loading', management: null, error: null });
  const [detailManagementMutation, setDetailManagementMutation] = useState({ scope: '', action: '', status: 'idle', message: '' });
  const [publishReconciliationRequired, setPublishReconciliationRequired] = useState(false);
  const [budgetState, setBudgetState] = useState({ scope: '', value: '', preserve: false });
  const [budgetNotice, setBudgetNotice] = useState(null);
  const [confirmationContext, setConfirmationContext] = useState(null);
  const [billingPrompt, setBillingPrompt] = useState(null);
  useEffect(() => setBillingPrompt(null), [companyId, campaignId, productPolicy]);
  const [archiveOverlay, setArchiveOverlay] = useState(null);
  const archiveFailureCancel = useRef(null);
  const confirmationDialog = useConfirmationDialog();
  const mutationVersion = useRef(0);
  const detailManagementRequestVersion = useRef(0);
  const detailManagementMutationVersion = useRef(0);
  const detailManagementRetryKeys = useRef(new Map());
  const detailManagementInFlight = useRef('');
  const detailProjectionRefreshInFlight = useRef('');
  const retryKeys = useRef(new Map());
  const trackedRequests = useRef(new Map());
  const publishInFlight = useRef('');
  const lifecycleInFlight = useRef('');
  const metaRecoveryInFlight = useRef('');
  const metaRecoveryVersion = useRef(0);
  const managementCommandScope = campaignCommandScopeKey(companyId, campaignId);
  const budgetScope = `${companyId}:${campaignId}`;
  const scope = `${companyId}:${campaignId}:${datePreset}`;
  const readScope = campaignDetailReadScopeKey(companyId, campaignId, datePreset);
  const scopeRef = useRef(scope);
  const readScopeRef = useRef(readScope);
  const latestReadRef = useRef({ scope: '', requestId: 0 });
  const mountedRef = useRef(false);
  const managementCommandScopeRef = useRef(managementCommandScope);
  const detailActionGuardRef = useRef(null);
  scopeRef.current = scope;
  readScopeRef.current = readScope;
  managementCommandScopeRef.current = managementCommandScope;
  const budget = budgetState.scope === budgetScope ? budgetState.value : '';
  useEffect(() => {
    if (archiveOverlay?.scope === managementCommandScope
      && ['error', 'warning'].includes(detailManagementMutation.status)) {
      archiveFailureCancel.current?.focus({ preventScroll: true });
    }
  }, [archiveOverlay, managementCommandScope, detailManagementMutation.status]);
  useEffect(() => {
    if (!budgetNotice) return undefined;
    const timer = setTimeout(() => setBudgetNotice((current) => current === budgetNotice ? null : current), 5000);
    return () => clearTimeout(timer);
  }, [budgetNotice]);

  const loadDetail = useCallback(async ({
    expectedScope = scope,
    preserve = false,
    replace = false,
  } = {}) => {
    const expectedReadScope = readScope;
    if (!companyId) {
      setDetailState({ scope: expectedScope, status: 'empty', detail: null, error: null });
      return null;
    }
    setDetailState((current) => ({
      scope: expectedScope,
      status: 'loading',
      detail: preserve && current.scope === expectedScope ? current.detail : null,
      error: null,
    }));
    let claim = null;
    try {
      claim = campaignDetailReadCoordinator.read(
        expectedReadScope,
        (signal) => api.get(
          `/api/saas/campaigns/${encodeURIComponent(campaignId)}/operations?datePreset=${encodeURIComponent(datePreset)}`,
          { ...tenantHeaders(companyId), signal },
        ),
        { replace },
      );
      latestReadRef.current = { scope: expectedReadScope, requestId: claim.requestId };
      const response = await claim.promise;
      if (!isCurrentCampaignDetailRead({
        mounted: mountedRef.current,
        currentScope: readScopeRef.current,
        expectedScope: expectedReadScope,
        currentRequestId: latestReadRef.current.scope === expectedReadScope
          ? latestReadRef.current.requestId
          : 0,
        requestId: claim.requestId,
      }) || scopeRef.current !== expectedScope) return null;
      const detail = validatedCampaignDetail(response, campaignId);
      const restoredRetry = hydrateRetryStateFromDetail(
        retryKeys.current,
        trackedRequests.current,
        { companyId, campaignId, detail },
      );
      setDetailState({ scope: expectedScope, status: 'ready', detail, error: null });
      setBudgetState((current) => campaignBudgetStateAfterDetail(current, {
        scope: budgetScope,
        detail,
        retryMonthlyMajor: restoredRetry?.tracked?.action === 'budget'
          ? restoredRetry.tracked.monthlyMajor
          : '',
      }));
      return detail;
    } catch (error) {
      const currentRead = latestReadRef.current;
      if (
        !mountedRef.current
        || readScopeRef.current !== expectedReadScope
        || currentRead.scope !== expectedReadScope
        || currentRead.requestId !== claim?.requestId
        || scopeRef.current !== expectedScope
      ) return null;
      setDetailState((current) => ({
        scope: expectedScope,
        status: 'error',
        detail: preserve && current.scope === expectedScope ? current.detail : null,
        error,
      }));
      return null;
    }
  }, [budgetScope, campaignId, companyId, datePreset, readScope, scope]);

  const loadDetailManagement = useCallback(async ({ preserve = false } = {}) => {
    const expectedScope = managementCommandScope;
    const version = ++detailManagementRequestVersion.current;
    if (!companyId || !campaignId) {
      setDetailManagementState({ scope: expectedScope, status: 'empty', management: null, error: null });
      return null;
    }
    setDetailManagementState((current) => ({
      scope: expectedScope,
      status: 'loading',
      management: preserve && current.scope === expectedScope ? current.management : null,
      error: null,
    }));
    try {
      const response = await curatedCampaignManagementApi.get({ companyId, campaignId });
      if (
        !mountedRef.current
        || managementCommandScopeRef.current !== expectedScope
        || detailManagementRequestVersion.current !== version
      ) return null;
      const management = normalizeCampaignManagementResponse(response, campaignId);
      setDetailManagementState({ scope: expectedScope, status: 'ready', management, error: null });
      return management;
    } catch (error) {
      if (
        !mountedRef.current
        || managementCommandScopeRef.current !== expectedScope
        || detailManagementRequestVersion.current !== version
      ) return null;
      setDetailManagementState((current) => ({
        scope: expectedScope,
        status: 'error',
        management: preserve && current.scope === expectedScope ? current.management : null,
        error,
      }));
      return null;
    }
  }, [campaignId, companyId, managementCommandScope]);

  const reconcileTrackedDetail = useCallback((refreshed, expectedScope, requestedCacheIdentity = '') => {
    if (!refreshed) return null;
    const selected = trackedOperationForDetailOrClearScope(
      retryKeys.current,
      trackedRequests.current,
      {
        budgetScope,
        detail: refreshed,
        requestedCacheIdentity,
      },
    );
    if (!selected) {
      if (scopeRef.current === expectedScope) {
        setMutation((current) => current.scope === expectedScope
          && current.status !== 'idle'
          && mutationClearedByDetailReconcile(current, selected)
          ? { scope: expectedScope, action: '', status: 'idle', message: '' }
          : current);
      }
      return null;
    }
    const { cacheIdentity, tracked } = selected;
    const durableResult = durableMutationReconciliation(
      refreshed,
      tracked.action,
      tracked.operationId,
    );
    const presentation = durableMutationPresentation(durableResult, tracked.verb);
    if (presentation.clearRetryKey) {
      retryKeys.current.delete(cacheIdentity);
      trackedRequests.current.delete(cacheIdentity);
      if (tracked.action === 'budget') {
        setBudgetState((current) => campaignBudgetStateAfterDetail(current, {
          scope: budgetScope,
          detail: refreshed,
          acceptDurable: true,
          expectedRequestedValue: tracked.monthlyMajor,
        }));
      }
    }
    if (scopeRef.current === expectedScope) {
      setMutation({
        scope: expectedScope,
        action: tracked.action,
        status: presentation.status,
        message: presentation.message,
      });
    }
    return { durableResult, presentation };
  }, [budgetScope]);

  const refreshDetail = useCallback(async ({ preserve = true, replace = false } = {}) => {
    const refreshed = await loadDetail({ expectedScope: scope, preserve, replace });
    if (refreshed) setPublishReconciliationRequired(false);
    reconcileTrackedDetail(refreshed, scope);
    return refreshed;
  }, [loadDetail, reconcileTrackedDetail, scope]);

  const refreshDetailProjections = useCallback(async () => {
    const refreshedDetail = await refreshDetail();
    const refreshedManagement = await loadDetailManagement({ preserve: true });
    return { detail: refreshedDetail, management: refreshedManagement };
  }, [loadDetailManagement, refreshDetail]);
  const retryDetailLoad = useCallback(
    () => refreshDetail({ preserve: true }),
    [refreshDetail],
  );

  useEffect(() => {
    mountedRef.current = true;
    refreshDetail({ preserve: false });
    return () => {
      mountedRef.current = false;
      campaignDetailReadCoordinator.scheduleAbort(readScope);
      mutationVersion.current += 1;
      detailManagementMutationVersion.current += 1;
      detailManagementRequestVersion.current += 1;
      lifecycleInFlight.current = '';
      metaRecoveryInFlight.current = '';
      metaRecoveryVersion.current += 1;
      detailManagementInFlight.current = '';
      detailProjectionRefreshInFlight.current = '';
    };
  }, [readScope, refreshDetail]);

  useEffect(() => {
    setDetailManagementMutation({ scope: managementCommandScope, action: '', status: 'idle', message: '' });
    loadDetailManagement();
    return () => { detailManagementRequestVersion.current += 1; };
  }, [loadDetailManagement, managementCommandScope]);

  const detail = detailState.scope === scope ? detailState.detail : null;
  // OGC-478: a refreshing continuity re-reads Details on its own, at most
  // twice per campaign while Details stays mounted, after the server's
  // suggested wait; after that the note asks for a manual refresh.
  const continuityRefetches = useRef(null);
  if (!continuityRefetches.current) continuityRefetches.current = createContinuityRefetchScheduler();
  const detailRefreshing = detailContinuityRefreshing(detail);
  const continuityRefreshExhausted = continuityRefetches.current.exhausted(campaignId);
  useEffect(() => {
    if (!detailRefreshing) return undefined;
    continuityRefetches.current.request(
      campaignId,
      continuityRefetchDelayMs(detailRefreshing.retryAfterSeconds),
      () => { if (mountedRef.current) loadDetail({ preserve: true }); },
    );
    return () => continuityRefetches.current.cancel();
  }, [campaignId, detail, loadDetail]);
  const currentMutation = mutation.scope === scope ? mutation : { action: '', status: 'idle', message: '' };
  const currentDetailManagement = detailManagementState.scope === managementCommandScope
    ? detailManagementState.management
    : null;
  const currentDetailManagementMutation = detailManagementMutation.scope === managementCommandScope
    ? detailManagementMutation
    : { action: '', status: 'idle', message: '' };
  useEffect(() => {
    if (currentDetailManagementMutation.action === 'archive' && currentDetailManagementMutation.status === 'success') setArchiveOverlay(null);
  }, [currentDetailManagementMutation.action, currentDetailManagementMutation.status]);
  const managementAllowsMetaControls = detailManagementState.scope === managementCommandScope
    && detailManagementState.status === 'ready'
    && campaignManagementAllowsMetaControls(detail, currentDetailManagement);
  const roleControls = campaignControlsForPolicy(
    managementAllowsMetaControls ? detail : null,
    productPolicy,
  );
  // Keep eligible spend controls visible so a blocked click can explain billing
  // locally. These booleans never grant permission to execute the action.
  const visibleControls = roleControls;
  const budgetAccess = campaignBudgetAccess({ billing, companyId, detail, monthlyMajor: canonicalMonthlyMajor(budget) || '' });
  const publishWorkflowJobId = exactText(detail?.configuration?.approval?.workflowJobId);
  const publishApprovalRequestId = exactText(
    detail?.configuration?.approval?.approvalRequestId,
  );
  const publishConfirmation = curatedMetaPublishConfirmation(
    curatedMetaPublishSnapshotFromDetail(detail),
    { retry: true },
  );
  const canRetryPublish = managementAllowsMetaControls
    && !publishReconciliationRequired
    && canRetryCuratedMetaPublish(detail, productPolicy)
    && billing.canCreate;
  const expectedPublishAdCount = Number.isSafeInteger(detail?.configuration?.structure?.adCount)
    ? detail.configuration.structure.adCount
    : null;
  const publishProgress = useCuratedMetaPublishProgress({
    campaignId,
    companyId,
    workflowJobId: publishWorkflowJobId,
    approvalRequestId: publishApprovalRequestId,
    ...curatedCampaignPublishProgressPollingOptions(detail?.campaign?.status, currentMutation, detail?.provider),
    totalAds: expectedPublishAdCount,
  });
  const publishProgressTerminalRefresh = useRef('');
  useEffect(() => {
    const progress = publishProgress.progress;
    const durableStatus = exactText(detail?.campaign?.status).toLowerCase();
    const terminalNeedsReconciliation = progress.state === 'succeeded'
      ? durableStatus !== 'paused'
      : progress.state === 'failed'
        ? durableStatus === 'publishing'
        : false;
    const pendingPublishMutation = currentMutation.action === 'publish' && currentMutation.status === 'pending';
    // OGC-409: when terminalNeedsReconciliation is the only reason this effect
    // would fire, the observed-status effect below is already reconciling the
    // exact same local/observed disagreement (see
    // curatedCampaignObservedStatusReconciliationKey) — completion always
    // reaches `succeeded`/`failed` with an observedCampaignStatus that
    // disagrees with the still-stale local status in that case, so both
    // effects would otherwise issue a concurrent, redundant
    // refreshDetailProjections() for the same transition. Defer to the
    // observed-status effect here. Never skip the OGC-404 resume/retry case
    // (a pending publish mutation) — that must keep reconciling on its own
    // regardless of whether the observed-status effect also happens to be
    // diverged right now.
    const observedStatusEffectWillHandleThis = terminalNeedsReconciliation
      && !pendingPublishMutation
      && Boolean(curatedCampaignObservedStatusReconciliationKey(
        detail?.campaign?.status,
        publishProgress.observedCampaignStatus,
      ));
    if (
      !['failed', 'succeeded'].includes(progress.state)
      || !(terminalNeedsReconciliation || pendingPublishMutation)
      || observedStatusEffectWillHandleThis
    ) return;
    const key = [scope, progress.state, progress.stage, progress.completedAds, progress.pausedAds].join(':');
    if (publishProgressTerminalRefresh.current === key) return;
    publishProgressTerminalRefresh.current = key;
    let active = true;
    let retryTimer = null;
    const reconcile = async (attempt = 0) => {
      const refreshed = await refreshDetailProjections();
      if (!active || publishProgressTerminalRefresh.current !== key) return;
      const refreshedStatus = exactText(refreshed.detail?.campaign?.status).toLowerCase();
      const reconciled = progress.state === 'succeeded'
        ? refreshedStatus === 'paused'
        : ['partial', 'failed'].includes(refreshedStatus);
      if (reconciled || attempt >= 2) return;
      retryTimer = setTimeout(() => reconcile(attempt + 1), 750);
    };
    reconcile();
    return () => {
      active = false;
      if (retryTimer) clearTimeout(retryTimer);
      if (publishProgressTerminalRefresh.current === key) {
        publishProgressTerminalRefresh.current = '';
      }
    };
  }, [
    currentMutation.action,
    currentMutation.status,
    detail?.campaign?.status,
    publishProgress.observedCampaignStatus,
    publishProgress.progress,
    refreshDetailProjections,
    scope,
  ]);
  // OGC-409: the local detail projection's campaign status can lag behind
  // what the live `/meta-publish-progress` poll itself observes — most
  // visibly across the seal->claim window, where the executor claims the
  // row (flipping it to `publishing`) 10-30s after Details loads, well past
  // any short fixed-retry budget. Reconcile the detail projection whenever
  // the two disagree (see `curatedCampaignObservedStatusReconciliationKey`
  // above) so status/controls/progress catch up without a manual reload.
  // Because the trigger is a *difference*, a successful refresh converges
  // the local status to the observed one and stillDiverged goes false on its
  // own; the small bounded retry here exists only to survive a failed or
  // still-stale refresh, mirroring the terminal-reconciliation effect above
  // so this can never loop. Divergence after the refresh is judged with
  // curatedCampaignObservedStatusStillDiverged rather than by re-deriving the
  // reconciliation key, because that key alone cannot tell a genuinely
  // reconciled refresh apart from a failed one — both read the local status
  // as empty.
  const observedStatusRefresh = useRef('');
  useEffect(() => {
    const key = curatedCampaignObservedStatusReconciliationKey(
      detail?.campaign?.status,
      publishProgress.observedCampaignStatus,
    );
    if (!key || observedStatusRefresh.current === key) return undefined;
    observedStatusRefresh.current = key;
    let active = true;
    let retryTimer = null;
    const reconcile = async (attempt = 0) => {
      const refreshed = await refreshDetailProjections();
      if (!active || observedStatusRefresh.current !== key) return;
      const stillDiverged = curatedCampaignObservedStatusStillDiverged(
        refreshed.detail?.campaign?.status,
        publishProgress.observedCampaignStatus,
      );
      if (!stillDiverged || attempt >= 2) return;
      retryTimer = setTimeout(() => reconcile(attempt + 1), 750);
    };
    reconcile();
    return () => {
      active = false;
      if (retryTimer) clearTimeout(retryTimer);
      if (observedStatusRefresh.current === key) observedStatusRefresh.current = '';
    };
  }, [detail?.campaign?.status, publishProgress.observedCampaignStatus, refreshDetailProjections]);
  detailActionGuardRef.current = {
    budget,
    billing,
    detail,
    campaignId,
    canRetryPublish,
    companyId,
    currentDetailManagement,
    currentDetailManagementMutation,
    currentMutation,
    detailManagementStateStatus: detailManagementState.scope === managementCommandScope
      ? detailManagementState.status
      : 'loading',
    detailStateStatus: detailState.scope === scope ? detailState.status : 'loading',
    managementCommandScope,
    productPolicy,
    publishConfirmation,
    publishWorkflowJobId,
    metaAccessRecoveryState: exactText(detail?.metaAccessRecovery?.state),
    campaignUpdatedAt: exactText(detail?.campaign?.updatedAt),
    publishIntentId: exactText(detail?.provider?.publishIntentId),
    scope,
    visibleControls,
  };

  async function refreshDetailAndManagement() {
    const expectedScope = managementCommandScope;
    if (detailProjectionRefreshInFlight.current) return null;
    detailProjectionRefreshInFlight.current = expectedScope;
    const action = currentDetailManagementMutation.action;
    const shouldReconcileManagement = CAMPAIGN_MANAGEMENT_ACTIONS.has(action)
      && ['warning', 'error'].includes(currentDetailManagementMutation.status);
    try {
      const refreshed = await refreshDetailProjections();
      if (
        !mountedRef.current
        || managementCommandScopeRef.current !== expectedScope
      ) return refreshed.detail;
      if (!shouldReconcileManagement) return refreshed.detail;
      const finalManagement = refreshed.management || currentDetailManagement;
      const presentation = campaignDetailManagementRefreshPresentation(action, {
        management: finalManagement,
        refreshComplete: Boolean(refreshed.detail && refreshed.management),
      });
      if (presentation.settled) {
        detailManagementRetryKeys.current.delete(`${expectedScope}:${action}`);
      }
      setDetailManagementMutation({
        scope: expectedScope,
        action,
        status: presentation.status,
        message: presentation.message,
      });
      return refreshed.detail;
    } finally {
      if (detailProjectionRefreshInFlight.current === expectedScope) {
        detailProjectionRefreshInFlight.current = '';
      }
    }
  }

  // OGC-455: `unobservable` is the second archive the owner can reach only
  // after the first was refused because Meta cannot see the campaign. It hides
  // the campaign here and changes nothing in Meta, so it asks for that in those
  // words rather than reusing the ordinary archive confirmation.
  async function performDetailManagementMutation(action, { unobservable = false, activePausedInMeta = false } = {}) {
    const detailBusy = detailState.status === 'loading'
      || currentMutation.status === 'pending'
      || Boolean(lifecycleInFlight.current)
      || Boolean(metaRecoveryInFlight.current)
      || Boolean(publishInFlight.current);
    if (!canStartCampaignDetailManagementMutation({
      action,
      detailBusy,
      management: currentDetailManagement,
      managementBusy: Boolean(detailManagementInFlight.current)
        || Boolean(detailProjectionRefreshInFlight.current)
        || currentDetailManagementMutation.status === 'pending'
        || detailManagementState.status !== 'ready',
      productPolicy,
    })) return;
    const baseDialogOptions = campaignManagementDialogOptions(action);
    if (!baseDialogOptions) return;
    const pauseBeforeArchive = action === 'archive' && ['active', 'partial', 'failed'].includes(detail?.campaign?.status);
    const campaignName = exactText(detail?.campaign?.name);
    if (action === 'delete' && !campaignName) return;
    // OGC-493: the owner's typed statement needs the name to type.
    if (activePausedInMeta && (action !== 'archive' || !unobservable || !campaignName)) return;
    const deleteMode = currentDetailManagement?.deleteEligibility?.mode === 'tombstone' ? 'tombstone' : 'hard';
    const dialogOptions = { ...baseDialogOptions,
      title: action === 'archive' ? (pauseBeforeArchive ? 'Pause & archive this campaign?' : 'Archive this campaign?')
        : action === 'restore' ? 'Restore this campaign?'
          : action === 'delete' ? (campaignName ? `Delete “${campaignName}”?` : baseDialogOptions.title)
            : baseDialogOptions.title,
      confirmLabel: pauseBeforeArchive ? 'Pause & archive' : baseDialogOptions.confirmLabel,
      ...(unobservable ? {
        eyebrow: 'Archive without Meta',
        title: 'Archive without changing Meta?',
        confirmLabel: 'Archive here',
        description: 'BeyondLeads can’t reach this campaign in Meta, so archiving here only removes it from your list. Its leads and history stay. If the campaign is still running in Meta, it keeps running and spending until you pause it in Meta Ads Manager.',
      } : {}),
      // OGC-493: BeyondLeads believes this campaign is delivering and cannot
      // check. The owner types its name to say they paused it in Meta.
      ...(activePausedInMeta ? {
        eyebrow: 'Archive without Meta',
        title: `Archive “${campaignName}” here only?`,
        confirmLabel: 'Archive here only',
        tone: 'danger',
        description: 'BeyondLeads can’t verify this campaign in Meta, so it can’t pause it or confirm it is paused. Archiving here only removes it from your list and changes nothing in Meta; its leads and history stay. If it is still running, it keeps delivering and spending until you pause it in Meta Ads Manager. Confirm only after you have paused it there.',
        confirmation: { label: 'Type the campaign name to confirm you paused it in Meta Ads Manager', expected: campaignName,
          hint: 'Enter the campaign name exactly as shown' },
      } : {}),
      ...(action === 'delete' ? campaignDeleteDialogOptions(detail, currentDetailManagement) : {}),
    };
    const expectedScope = managementCommandScope;
    const expectedRevision = currentDetailManagement.configurationRevision;
    setConfirmationContext({ scope: managementCommandScope, action });
    const approved = await confirmationDialog.request(dialogOptions);
    // OGC-452: the server re-checks what was typed, not the name we showed.
    // Only the invisible leading/trailing whitespace is dropped: the server
    // bounds the raw value at the stored name's maximum length, so a
    // max-length name typed with padding would otherwise be refused as
    // invalid even though it matches.
    const typedConfirmationName = typeof approved?.typedValue === 'string' ? approved.typedValue.trim() : campaignName;
    const latest = detailActionGuardRef.current;
    const latestDetailBusy = latest?.detailStateStatus === 'loading'
      || latest?.currentMutation?.status === 'pending'
      || Boolean(lifecycleInFlight.current)
      || Boolean(metaRecoveryInFlight.current)
      || Boolean(publishInFlight.current);
    const latestManagementBusy = Boolean(detailManagementInFlight.current)
      || Boolean(detailProjectionRefreshInFlight.current)
      || latest?.currentDetailManagementMutation?.status === 'pending'
      || latest?.detailManagementStateStatus !== 'ready';
    if (
      !approved
      || !mountedRef.current
      || latest?.companyId !== companyId
      || latest?.campaignId !== campaignId
      || latest?.scope !== scope
      || latest?.managementCommandScope !== expectedScope
      || latest?.currentDetailManagement?.configurationRevision !== expectedRevision
      || !canStartCampaignDetailManagementMutation({
        action,
        detailBusy: latestDetailBusy,
        management: latest?.currentDetailManagement,
        managementBusy: latestManagementBusy,
        productPolicy: latest?.productPolicy,
      })
      || managementCommandScopeRef.current !== expectedScope
    ) return;
    const requestScope = JSON.stringify([managementCommandScope, action]);
    if (detailManagementInFlight.current) return;
    detailManagementInFlight.current = requestScope;
    if (action === 'archive') setArchiveOverlay({ scope: managementCommandScope, pausing: pauseBeforeArchive && !unobservable, providerWait: !unobservable && currentDetailManagement?.archiveWarning?.reasonCode !== 'campaign_archive_draft_only' });
    const retryIdentity = `${managementCommandScope}:${action}`;
    let idempotencyKey = detailManagementRetryKeys.current.get(retryIdentity);
    if (!idempotencyKey) {
      idempotencyKey = campaignManagementRetryKey(action, companyId, campaignId);
      detailManagementRetryKeys.current.set(retryIdentity, idempotencyKey);
    }
    const version = ++detailManagementMutationVersion.current;
    setDetailManagementMutation({
      scope: expectedScope,
      action,
      status: 'pending',
      message: action === 'delete'
        ? (deleteMode === 'tombstone' ? 'Deleting in Meta…' : 'Deletion is being confirmed.')
        : `${action[0].toUpperCase()}${action.slice(1)} is being confirmed.`,
    });
    const isCurrent = () => isCurrentCampaignDetailManagementMutation({
      mounted: mountedRef.current,
      currentScope: managementCommandScopeRef.current,
      expectedScope,
      currentVersion: detailManagementMutationVersion.current,
      version,
    });
    try {
      const input = {
        companyId,
        campaignId,
        expectedRevision,
        idempotencyKey,
      };
      const response = action === 'archive'
        ? await curatedCampaignManagementApi.archive(
          unobservable ? {
            ...input,
            acknowledgeUnobservableArchive: true,
            ...(activePausedInMeta ? { acknowledgePausedInMeta: true, confirmationName: typedConfirmationName } : {}),
          } : input,
        )
        : action === 'restore'
          ? await curatedCampaignManagementApi.restore(input)
          : await curatedCampaignManagementApi.delete({ ...input, confirmed: true, confirmationName: typedConfirmationName });
      if (!isCurrent()) return;
      if (action === 'delete') {
        normalizeCampaignDeletionResponse(response, campaignId);
        detailManagementRetryKeys.current.delete(retryIdentity);
        queueCampaignListNotice(companyId, {
          status: 'success',
          message: campaignName ? `“${campaignName}” was deleted.` : 'The campaign was deleted.',
        });
        navigate?.('campaigns');
        return;
      }

      const responseManagement = normalizeCampaignManagementResponse(response, campaignId);
      if (campaignManagementCommandSettled(action, responseManagement)) {
        detailManagementRetryKeys.current.delete(retryIdentity);
      }
      setDetailManagementState({
        scope: expectedScope,
        status: 'ready',
        management: responseManagement,
        error: null,
      });
      const refreshedDetail = await refreshDetail({ preserve: true, replace: true });
      const refreshedManagement = await loadDetailManagement({ preserve: true });
      if (!isCurrent()) return;
      const finalManagement = refreshedManagement || responseManagement;
      const presentation = campaignDetailManagementRefreshPresentation(action, {
        management: finalManagement,
        refreshComplete: Boolean(refreshedDetail && refreshedManagement),
      });
      if (presentation.settled) detailManagementRetryKeys.current.delete(retryIdentity);
      setDetailManagementMutation({
        scope: expectedScope,
        action,
        status: presentation.status,
        message: presentation.message,
      });
    } catch (error) {
      if (!isCurrent()) return;
      if (!retainCampaignManagementRetryKey(error)) {
        detailManagementRetryKeys.current.delete(retryIdentity);
      }
      if (action === 'archive') setArchiveOverlay((current) => current?.scope === expectedScope
        ? {
          ...current,
          failure: error?.code === 'campaign_archive_pause_unconfirmed' ? 'pause_unconfirmed' : error?.code === 'campaign_archive_pause_connection_required' ? 'pause_connection_required' : error?.code === 'campaign_archive_pause_rate_limited' ? 'pause_rate_limited' : 'unconfirmed_outcome',
          // OGC-455: only the server decides this, and only for a campaign it
          // could not read that we do not believe is delivering.
          localArchiveAvailable: error?.code === 'campaign_archive_pause_connection_required'
            && error?.localArchiveAvailable === true,
          // OGC-493: an active campaign the connection can never reach again.
          activeArchiveAvailable: error?.code === 'campaign_archive_pause_connection_required'
            && error?.activeArchiveAvailable === true,
        }
        : current);
      // OGC-452: an incomplete Meta delete is a state the drawer renders (what
      // remains, why, when to retry); reload management so it shows it.
      const incompleteDelete = action === 'delete' && error?.code === 'campaign_delete_incomplete';
      if (incompleteDelete) {
        detailManagementRetryKeys.current.delete(retryIdentity);
        await loadDetailManagement({ preserve: true });
        if (!isCurrent()) return;
      }
      setDetailManagementMutation({
        scope: expectedScope,
        action,
        status: incompleteDelete ? 'warning' : 'error',
        message: safeFailureMessage(error, 'curated-campaign'),
      });
    } finally {
      invalidateCampaignManagementList(companyId);
      if (detailManagementInFlight.current === requestScope) detailManagementInFlight.current = '';
    }
  }

  async function recoverMetaAccess() {
    const expectedScope = scope;
    const expectedCampaignUpdatedAt = exactText(detail?.campaign?.updatedAt);
    const expectedPublishIntentId = exactText(detail?.provider?.publishIntentId);
    if (
      detail?.metaAccessRecovery?.state !== 'available'
      || !expectedCampaignUpdatedAt
      || !expectedPublishIntentId
      || currentMutation.status === 'pending'
      || lifecycleInFlight.current
      || publishInFlight.current
      || metaRecoveryInFlight.current
      || detailManagementInFlight.current
      || detailProjectionRefreshInFlight.current
      || currentDetailManagementMutation.status === 'pending'
    ) return;
    const expected = campaignMetaAccessRecoverySnapshot({
      campaignId,
      campaignUpdatedAt: expectedCampaignUpdatedAt,
      companyId,
      publishIntentId: expectedPublishIntentId,
      scope: expectedScope,
    });
    setConfirmationContext({ scope: managementCommandScope, action: 'recover_meta_access' });
    await executeCampaignMetaAccessRecovery({
      expected,
      inFlight: metaRecoveryInFlight,
      versionRef: metaRecoveryVersion,
      requestConfirmation: () => confirmationDialog.request({
        eyebrow: 'Meta campaign access',
        title: 'Verify campaign access?',
        description: 'BeyondLeads will read your current Meta authorization and the exact ad account, Facebook Page, Instant Form, and optional Instagram identity sealed with this campaign. It will not change the Page, Form, campaign status, ads, budget, delivery, or spend.',
        confirmLabel: 'Verify campaign access',
        tone: 'accent',
      }),
      readCurrentContext: () => {
        const latest = detailActionGuardRef.current;
        return {
          mounted: mountedRef.current,
          companyId: latest?.companyId,
          campaignId: latest?.campaignId,
          scope: latest?.scope,
          metaAccessRecoveryState: latest?.metaAccessRecoveryState,
          campaignUpdatedAt: latest?.campaignUpdatedAt,
          publishIntentId: latest?.publishIntentId,
          blocked: latest?.currentMutation?.status === 'pending'
            || latest?.currentDetailManagementMutation?.status === 'pending'
            || scopeRef.current !== expectedScope
            || Boolean(lifecycleInFlight.current)
            || Boolean(publishInFlight.current)
            || Boolean(detailManagementInFlight.current)
            || Boolean(detailProjectionRefreshInFlight.current),
        };
      },
      postRecovery: (body) => api.post(
        `/api/saas/campaigns/${encodeURIComponent(campaignId)}/operations/recover-meta-access`,
        body,
        tenantHeaders(companyId),
      ),
      refreshDetail: () => loadDetail({ expectedScope, preserve: true, replace: true }),
      refreshManagement: () => loadDetailManagement({ preserve: true }),
      present: (presentation) => setMutation({ scope: expectedScope, ...presentation }),
      safeFailure: (error) => safeFailureMessage(error, 'curated-campaign'),
    });
  }

  async function performMutation(action) {
    const definition = ACTIONS[action];
    if (
      !definition
      || !visibleControls[action]
      || currentMutation.status === 'pending'
      || lifecycleInFlight.current
      || metaRecoveryInFlight.current
      || detailManagementInFlight.current
      || detailProjectionRefreshInFlight.current
      || currentDetailManagementMutation.status === 'pending'
    ) return;
    if (['activate', 'resume'].includes(action) && !campaignSpendAccess(billing, companyId).allowed) {
      setBillingPrompt({ companyId, campaignId, action });
      return;
    }
    const normalizedBudget = action === 'budget' ? canonicalMonthlyMajor(budget) : '';
    if (action === 'budget' && !normalizedBudget) {
      setMutation({ scope, action, status: 'error', message: 'Enter a valid monthly budget with no more than two decimal places.' });
      return;
    }
    const currency = campaignBudgetBaseline(detail)?.currency || exactText(
      detail?.metrics?.account?.currency,
      exactText(detail?.configuration?.budget?.currency),
    ).toUpperCase();
    const currentBudgetAccess = action === 'budget' ? campaignBudgetAccess({ billing, companyId, detail, monthlyMajor: normalizedBudget }) : null;
    if (currentBudgetAccess && !currentBudgetAccess.allowed) {
      setBillingPrompt({ companyId, campaignId, action: 'budget' });
      return;
    }
    if (action === 'budget' && currentBudgetAccess.unchanged) {
      // Presentation only: no confirmation, retry identity, or provider operation is created.
      setBudgetNotice({ scope: budgetScope, message: 'The budget is unchanged.' });
      return;
    }
    setBudgetNotice(null);
    const confirmation = action === 'budget'
      ? `Update the campaign-level monthly budget to ${normalizedBudget}${/^[A-Z]{3}$/.test(currency) ? ` ${currency}` : ''} on Meta?`
      : definition.confirmation;
    const dialogOptions = campaignLifecycleDialogOptions(action, action === 'budget'
      ? `BeyondLeads will ask Meta to change this campaign's monthly budget to ${normalizedBudget}${/^[A-Z]{3}$/.test(currency) ? ` ${currency}` : ''}. The change affects this campaign's potential spend while it is eligible to deliver.`
      : confirmation);
    if (!dialogOptions) return;
    if (action === 'budget') {
      const baseline = campaignBudgetBaseline(detail);
      dialogOptions.description = `Saved monthly planning budget: ${detailMoney(detail?.configuration?.budget?.monthlyMajor, detail?.configuration?.budget?.currency)}. Set ${detail?.campaign?.name || 'this campaign'} to ${detailMoney(normalizedBudget, currency)} per month (about ${detailMoney(Number(normalizedBudget) / 30.4, currency)}/day).${baseline ? ` Meta’s current verified daily budget is ${(baseline.dailyMinor / (10 ** baseline.minorUnitExponent)).toFixed(baseline.minorUnitExponent)} ${baseline.currency}.` : ''}`;
    }
    const expectedScope = scope;
    setConfirmationContext({ scope: managementCommandScope, action, monthlyMajor: normalizedBudget, currency, previousMonthlyMajor: detail?.configuration?.budget?.monthlyMajor });
    const approved = await confirmationDialog.request(dialogOptions);
    const latest = detailActionGuardRef.current;
    if (
      !approved
      || !mountedRef.current
      || latest?.companyId !== companyId
      || latest?.campaignId !== campaignId
      || latest?.scope !== expectedScope
      || latest?.managementCommandScope !== managementCommandScope
      || latest?.visibleControls?.[action] !== true
      || !hasNamedTenantAction(latest?.productPolicy, definition.productAction)
      || latest?.currentMutation?.status === 'pending'
      || latest?.currentDetailManagementMutation?.status === 'pending'
      || (action === 'budget' && canonicalMonthlyMajor(latest?.budget) !== normalizedBudget)
      || (action === 'budget' && !campaignBudgetAccess({ billing: latest?.billing, companyId, detail: latest?.detail, monthlyMajor: normalizedBudget }).allowed)
      || (action === 'budget' && (campaignBudgetBaseline(latest?.detail)?.currency || exactText(latest?.detail?.metrics?.account?.currency, exactText(latest?.detail?.configuration?.budget?.currency)).toUpperCase()) !== currency)
      || (['activate', 'resume'].includes(action) && !campaignSpendAccess(latest?.billing, companyId).allowed)
      || scopeRef.current !== expectedScope
      || lifecycleInFlight.current
      || metaRecoveryInFlight.current
      || detailManagementInFlight.current
      || detailProjectionRefreshInFlight.current
    ) return;
    if (action === 'budget' && campaignBudgetAccess({ billing: latest.billing, companyId, detail: latest.detail, monthlyMajor: normalizedBudget }).unchanged) {
      setBudgetNotice({ scope: budgetScope, message: 'The budget is unchanged.' });
      return;
    }
    const retry = scopedOperationRetryKey(retryKeys.current, {
      companyId,
      campaignId,
      action,
      monthlyMajor: normalizedBudget,
    });
    const idempotencyKey = retry.idempotencyKey;
    const inFlightScope = JSON.stringify([scope, action, idempotencyKey]);
    if (!claimLifecycleMutation(lifecycleInFlight, inFlightScope)) return;
    if (action === 'budget') {
      setBudgetState({ scope: budgetScope, value: normalizedBudget, preserve: true });
    }

    const version = ++mutationVersion.current;
    setMutation({ scope: expectedScope, action, status: 'pending', message: `${definition.verb} request is being confirmed by Meta.` });
    try {
      const response = await api.post(
        `/api/saas/campaigns/${encodeURIComponent(campaignId)}/operations/${action}`,
        mutationBody(action, idempotencyKey, normalizedBudget),
        tenantHeaders(companyId),
      );
      if (version !== mutationVersion.current || scopeRef.current !== expectedScope) return;
      const operationId = mutationOperationId(response);
      if (!operationId) {
        await loadDetail({ expectedScope, preserve: true, replace: true });
        await loadDetailManagement({ preserve: true });
        if (version !== mutationVersion.current || scopeRef.current !== expectedScope) return;
        setMutation({
          scope: expectedScope,
          action,
          status: 'error',
          message: 'The request was accepted, but its exact operation status is unavailable. Retry the same action or refresh.',
        });
        return;
      }
      for (const [cacheIdentity, tracked] of trackedRequests.current) {
        if (
          cacheIdentity !== retry.cacheIdentity
          && tracked.budgetScope === budgetScope
          && tracked.action === action
        ) {
          trackedRequests.current.delete(cacheIdentity);
          retryKeys.current.delete(cacheIdentity);
        }
      }
      trackedRequests.current.set(retry.cacheIdentity, {
        action,
        budgetScope,
        monthlyMajor: normalizedBudget,
        operationId,
        verb: definition.verb,
      });
      const refreshed = await loadDetail({ expectedScope, preserve: true, replace: true });
      await loadDetailManagement({ preserve: true });
      if (version !== mutationVersion.current || scopeRef.current !== expectedScope) return;
      if (refreshed) {
        reconcileTrackedDetail(refreshed, expectedScope, retry.cacheIdentity);
      } else {
        setMutation({ scope: expectedScope, action, status: 'error', message: 'The request was accepted, but its latest status is unavailable. Retry with the same action or refresh.' });
      }
    } catch (error) {
      if (version !== mutationVersion.current || scopeRef.current !== expectedScope) return;
      if (error?.code === 'billing_subscription_required') {
        await billing.refresh();
        if (version !== mutationVersion.current || scopeRef.current !== expectedScope) return;
        setBillingPrompt({ companyId, campaignId, action, serverDenied: true });
      }
      if (version !== mutationVersion.current || scopeRef.current !== expectedScope) return;
      setMutation({ scope: expectedScope, action, status: 'error', message: safeFailureMessage(error, 'curated-campaign') });
    } finally {
      releaseLifecycleMutation(lifecycleInFlight, inFlightScope);
    }
  }

  async function retryMetaPublish() {
    if (!await billing.requestAccess('create')) return;
    if (
      !canRetryPublish
      || currentMutation.status === 'pending'
      || metaRecoveryInFlight.current
      || detailManagementInFlight.current
      || detailProjectionRefreshInFlight.current
      || currentDetailManagementMutation.status === 'pending'
    ) return;
    const requestScope = curatedMetaPublishScopeKey(companyId, campaignId, publishWorkflowJobId);
    if (!requestScope || publishInFlight.current) return;
    const expectedScope = scope;
    setConfirmationContext({ scope: managementCommandScope, action: 'publish' });
    const approved = await confirmationDialog.request({
      eyebrow: 'Meta creation',
      title: 'Resume Meta creation?',
      description: confirmationConsequence(publishConfirmation),
      confirmLabel: 'Resume Meta creation',
      tone: 'warning',
    });
    const latest = detailActionGuardRef.current;
    if (
      !approved
      || !mountedRef.current
      || latest?.companyId !== companyId
      || latest?.campaignId !== campaignId
      || latest?.scope !== expectedScope
      || latest?.managementCommandScope !== managementCommandScope
      || latest?.canRetryPublish !== true
      || latest?.publishWorkflowJobId !== publishWorkflowJobId
      || latest?.publishConfirmation !== publishConfirmation
      || latest?.currentMutation?.status === 'pending'
      || latest?.currentDetailManagementMutation?.status === 'pending'
      || scopeRef.current !== expectedScope
      || lifecycleInFlight.current
      || metaRecoveryInFlight.current
      || detailManagementInFlight.current
      || detailProjectionRefreshInFlight.current
      || publishInFlight.current
    ) return;
    publishInFlight.current = requestScope;
    setPublishReconciliationRequired(false);
    const version = ++mutationVersion.current;
    setMutation({
      scope: expectedScope,
      action: 'publish',
      status: 'pending',
      message: 'The existing Meta creation attempt is resuming with its durable checkpoints.',
    });
    publishProgress.begin(expectedPublishAdCount);
    await yieldCuratedMetaPublishProgressPaint();
    if (
      scopeRef.current !== expectedScope
      || publishInFlight.current !== requestScope
    ) return;
    let requestError = null;
    try {
      const execution = resumeCuratedPausedCreation({ companyId, campaignId, workflowJobId: publishWorkflowJobId });
      publishProgress.reconnect();
      await execution;
    } catch (error) {
      requestError = error;
    }
    if (version !== mutationVersion.current || scopeRef.current !== expectedScope) {
      if (publishInFlight.current === requestScope) publishInFlight.current = '';
      return;
    }
    const refreshed = await loadDetail({ expectedScope, preserve: true, replace: true });
    await loadDetailManagement({ preserve: true });
    if (publishInFlight.current === requestScope) publishInFlight.current = '';
    if (version !== mutationVersion.current || scopeRef.current !== expectedScope) return;
    if (!refreshed) {
      setPublishReconciliationRequired(true);
      setMutation({
        scope: expectedScope,
        action: 'publish',
        status: 'error',
        message: 'Meta creation could not be reconciled. Refresh the durable status before retrying.',
      });
      return;
    }
    setPublishReconciliationRequired(false);
    const durableStatus = exactText(refreshed?.campaign?.status).toLowerCase();
    if (durableStatus === 'paused') {
      setMutation({
        scope: expectedScope,
        action: 'publish',
        status: 'success',
        message: 'Meta confirmed the campaign, shared ad set, and approved ads in PAUSED state.',
      });
      return;
    }
    if (durableStatus === 'publishing') {
      setMutation({
        scope: expectedScope,
        action: 'publish',
        status: 'warning',
        message: 'Meta creation is still in progress. Refresh status to reconcile the durable result.',
      });
      return;
    }
    if (['partial', 'failed'].includes(durableStatus)) {
      setMutation({
        scope: expectedScope,
        action: 'publish',
        status: 'error',
        message: requestError
          ? safeFailureMessage(requestError, 'curated-campaign')
          : 'Meta creation remains incomplete. Retry resumes the same attempt and creates only missing objects.',
      });
      return;
    }
    setMutation({
      scope: expectedScope,
      action: 'publish',
      status: 'error',
      message: requestError
        ? safeFailureMessage(requestError, 'curated-campaign')
        : 'The durable Meta creation status is unavailable. Refresh before retrying.',
    });
  }

  if (!companyId) {
    return <section className="bl-campaigns-page"><CampaignNotice title="Choose a company first" body="Select an active company to view this campaign." /></section>;
  }

  return (
    <section className="bl-campaigns-page bl-campaign-detail-page" aria-labelledby="curated-campaign-detail-title">
      {!detail && <CampaignDetailHeader campaign={detail?.campaign} configuration={detail?.configuration} navigate={navigate} />}

      {detailState.status === 'loading' && !detail && <CampaignNotice loading title="Loading campaign" body="Reconciling the campaign’s latest operational view." />}
      {detailState.status === 'empty' && <CampaignNotice title="Choose a company first" body="Select an active company to view this campaign." />}
      {detailState.status === 'error' && <CampaignDetailLoadFailure
        error={detailState.error}
        hasDetail={Boolean(detail)}
        onRetry={retryDetailLoad}
      />}
      {detail && <CampaignDetailView
        companyId={companyId}
        budget={budget}
        budgetNotice={budgetNotice?.scope === budgetScope ? budgetNotice : null}
        budgetAccess={budgetAccess}
        canOpenSetup={hasNamedTenantAction(productPolicy, 'campaign.draft.edit')}
        controls={visibleControls}
        canRetryPublish={canRetryPublish}
        confirmationOpen={confirmationDialog.dialogProps.open}
        datePreset={datePreset}
        detail={detail}
        loading={detailState.status === 'loading'}
        management={currentDetailManagement}
        managementBusyAction={currentDetailManagementMutation.status === 'pending'
          ? currentDetailManagementMutation.action
          : currentMutation.status === 'pending'
            ? 'provider'
            : detailState.status === 'loading' || detailManagementState.status === 'loading'
              ? 'refresh'
              : detailManagementState.status === 'error' ? 'stale' : ''}
        managementFeedback={currentDetailManagementMutation.status !== 'idle'
          ? currentDetailManagementMutation
          : null}
        managementStatus={detailManagementState.scope === managementCommandScope
          ? detailManagementState.status
          : 'loading'}
        mutation={currentMutation}
        publishProgress={publishProgress.progress}
        navigate={navigate}
        onBudgetChange={(value) => {
          setBudgetNotice(null);
          setBudgetState({ scope: budgetScope, value, preserve: true });
        }}
        onDatePresetChange={setDatePreset}
        onArchive={() => performDetailManagementMutation('archive')}
        onDelete={() => performDetailManagementMutation('delete')}
        onMutation={performMutation}
        onRetryManagement={refreshDetailAndManagement}
        onRetryPublish={retryMetaPublish}
        onRecoverMetaAccess={recoverMetaAccess}
        onRefresh={refreshDetailAndManagement}
        onRestore={() => performDetailManagementMutation('restore')}
        continuityRefreshExhausted={continuityRefreshExhausted}
        productPolicy={productPolicy}
      />}

      {billingPrompt?.companyId === companyId && billingPrompt.campaignId === campaignId && hasNamedTenantAction(productPolicy, ACTIONS[billingPrompt.action]?.productAction) && <CampaignBillingAccessDialog
        serverDenied={billingPrompt.serverDenied}
        decision={billingPrompt.action === 'budget' ? budgetAccess : campaignSpendAccess(billing, companyId)}
        onClose={() => setBillingPrompt(null)} onViewBilling={() => { setBillingPrompt(null); navigate?.('billing'); }}
        onRefresh={async () => { await billing.refresh(); setBillingPrompt(null); }} />}

      <CampaignConfirmationDialog {...confirmationDialog.dialogProps}
        icon={<CampaignDetailActionIcon action={confirmationContext?.scope === managementCommandScope ? confirmationContext.action : ''} />}>
        {confirmationContext?.scope === managementCommandScope && confirmationContext.action === 'budget' && <div className="bl-campaign-action-notice">
          A monthly planning budget, not a hard spending cap. The campaign keeps its current delivery status. All enabled ads share this budget.
        </div>}
      </CampaignConfirmationDialog>
      {currentDetailManagementMutation.action === 'delete' && currentDetailManagementMutation.status === 'pending' && <CampaignModal
        title="Deleting campaign…" description="Keep this page open while deletion is confirmed."
        icon={<CampaignDetailActionIcon action="delete" />} busy onClose={() => {}}
        footer={<button type="button" disabled>Deleting…</button>}>
        <Beacon size="control" label={currentDetailManagementMutation.message} delayMs={0}
          slowAfterMs={currentDetailManagement?.deleteEligibility?.mode === 'tombstone' ? 8000 : null} />
      </CampaignModal>}
      {archiveOverlay?.scope === managementCommandScope && <CampaignModal
        title={currentDetailManagementMutation.status === 'pending' ? (archiveOverlay.pausing ? 'Pausing & archiving…' : 'Archiving campaign…') : ['pause_unconfirmed', 'pause_connection_required', 'pause_rate_limited'].includes(archiveOverlay.failure) ? 'Campaign wasn’t archived.' : 'Archive needs attention'}
        description={currentDetailManagementMutation.status === 'pending'
          ? currentDetailManagementMutation.message || 'The campaign archive is being confirmed.'
          : archiveOverlay.failure === 'pause_unconfirmed'
            ? 'Delivery could not be confirmed paused. The campaign remains visible.'
            : archiveOverlay.failure === 'pause_connection_required'
              ? (archiveOverlay.localArchiveAvailable
              ? 'Meta can’t show us this campaign, so it can’t be confirmed paused. The campaign remains visible.'
              : archiveOverlay.activeArchiveAvailable
                ? 'BeyondLeads can’t verify this campaign in Meta, and it may still be delivering. The campaign remains visible.'
                : 'The current Meta connection could not be used for this campaign. The campaign remains visible.')
              : archiveOverlay.failure === 'pause_rate_limited'
                ? 'Meta is rate limiting this ad account. The campaign remains visible; nothing was changed.'
                : 'The archive outcome could not be confirmed. Review the current campaign state before trying again.'}
        icon={<CampaignDetailActionIcon action="archive" />}
        busy={currentDetailManagementMutation.status === 'pending'}
        onClose={() => setArchiveOverlay(null)}
        footer={currentDetailManagementMutation.status === 'pending'
          ? <button type="button" data-campaign-safe-focus disabled>Please wait</button>
          : <><button type="button" ref={archiveFailureCancel} data-campaign-safe-focus onClick={() => setArchiveOverlay(null)}>Cancel</button>{archiveOverlay.localArchiveAvailable
            ? <button type="button" data-campaign-archive-local onClick={() => {
              setArchiveOverlay(null);
              performDetailManagementMutation('archive', { unobservable: true });
            }}>Archive without changing Meta</button>
            : archiveOverlay.activeArchiveAvailable
              ? <button type="button" data-campaign-archive-active-local onClick={() => {
                setArchiveOverlay(null);
                performDetailManagementMutation('archive', { unobservable: true, activePausedInMeta: true });
              }}>I paused it in Meta — archive here</button>
              : null}<button type="button" className="is-primary" onClick={() => setArchiveOverlay(null)}>View details</button></>}>
        <Beacon size="control" active={currentDetailManagementMutation.status === 'pending'} label={archiveOverlay.pausing ? 'Confirming paused delivery…' : 'Confirming archive…'} delayMs={0} slowAfterMs={archiveOverlay.providerWait ? 8000 : null} />
        <div className="bl-campaign-action-notice" role={currentDetailManagementMutation.status === 'error' ? 'alert' : 'status'}>
          {currentDetailManagementMutation.status === 'pending'
            ? (archiveOverlay.providerWait ? 'Published delivery must be confirmed paused before archiving. Campaign history and individual ad choices are preserved.' : 'Archiving in BeyondLeads. Leads and campaign history are preserved.')
            : archiveOverlay.localArchiveAvailable || archiveOverlay.activeArchiveAvailable
              ? <><strong>Two ways forward</strong>{currentDetailManagementMutation.message}</>
              : <><strong>Review before retrying</strong>{currentDetailManagementMutation.message} Review delivery and recovery in campaign details. Archiving again requires a fresh review of current controls.</>}
        </div>
      </CampaignModal>}
    </section>
  );
}

export function CampaignDetailLoadFailure({ error, hasDetail, onRetry }) {
  const message = error?.code === 'curated_campaign_detail_timeout'
    ? 'Campaign status took too long to load. Retry without refreshing the page.'
    : safeFailureMessage(error, 'curated-campaign');
  if (hasDetail) {
    return (
      <div className="bl-campaign-operation-feedback is-error" role="alert">
        <strong>Latest reconciliation is unavailable</strong>
        <span>{message}</span>
        <button type="button" className="is-secondary" onClick={onRetry}>Retry refresh</button>
      </div>
    );
  }
  return (
    <div className="bl-campaign-list-notice" role="alert">
      <strong>Campaign is temporarily unavailable</strong>
      <span>{message}</span>
      <button type="button" className="is-secondary" onClick={onRetry}>Retry loading campaign</button>
    </div>
  );
}

export function CampaignDetailHeader({ campaign, configuration = {}, navigate, archived = false, actions }) {
  const renovationTypes = (Array.isArray(configuration.renovationTypes) ? configuration.renovationTypes : [])
    .map((type) => typeof type === 'string' ? type : type.name || type.label || type.key || type.id).filter(Boolean);
  const currency = exactText(configuration.budget?.currency).toUpperCase();
  const monthlyAmount = Number(configuration.budget?.monthlyMajor);
  const budgetLabel = configuration.budget?.monthlyMajor != null && Number.isFinite(monthlyAmount) && /^[A-Z]{3}$/.test(currency)
    ? `${number(monthlyAmount, { style: 'currency', currency })} / month` : '';
  const included = configuration.targeting?.includedCities || [];
  const area = included.map((city) => typeof city === 'string' ? city : city.label || city.name).filter(Boolean).join(', ') || exactText(configuration.targeting?.country);
  return (
    <header className="bl-campaigns-heading bl-campaign-detail-heading">
      <div>
        <button className="bl-campaign-back" type="button" onClick={() => navigate?.('campaigns')}>← All campaigns</button>
        <div className="bl-kicker">Campaign details{renovationTypes.length ? ` / ${renovationTypes.join(' + ')}` : ' · Curated Meta Ads'}</div>
        <h1 id="curated-campaign-detail-title">{campaign?.name || 'Campaign detail'}</h1>
        <p className="bl-detail-heading-context">
          {campaign?.status && <CampaignStatePill tone={campaignStatusTone(campaign.status)} className={`bl-campaign-state is-${campaign.status}`}>{statusLabel(campaign.status)}</CampaignStatePill>}
          {archived && <CampaignStatePill tone="neutral" className="bl-campaign-workspace-state">Archived</CampaignStatePill>}
          {(area || budgetLabel) && <span>{[area, budgetLabel].filter(Boolean).join(' · ')}</span>}
        </p>
      </div>
      {actions && <div className="bl-detail-heading-actions">{actions}</div>}
    </header>
  );
}

export function MetaCampaignTargetPanel({ target }) {
  const presentation = normalizeMetaCampaignTargetPresentation(target);
  if (!presentation) return null;
  const destination = presentation.destination;
  const rows = [
    ['Ad account', destination.adAccount.label],
    ['Facebook Page', destination.page.label],
    ['Instant Form', destination.instantForm.label],
    ['Instagram', destination.instagramIdentity?.label || 'Not selected'],
  ];
  return (
    <section className="bl-campaign-operations-card" aria-labelledby="meta-campaign-target-title">
      <div className="bl-campaign-card-heading">
        <div>
          <div className="bl-kicker">Saved destination</div>
          <h2 id="meta-campaign-target-title">Meta destination</h2>
        </div>
        <CampaignStatePill tone="neutral">{presentation.statusLabel}</CampaignStatePill>
      </div>
      <div className="bl-campaign-operation-feedback" role="status">
        <strong>{presentation.title}</strong>
        <span>{presentation.body}</span>
      </div>
      <dl className="bl-campaign-facts is-compact">
        {rows.map(([label, value]) => (
          <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
        ))}
      </dl>
    </section>
  );
}

export function CampaignDetailView({
  companyId = '',
  budget,
  budgetNotice = null,
  budgetAccess = null,
  canOpenSetup = false,
  canRetryPublish = false,
  confirmationOpen = false,
  continuityRefreshExhausted = false,
  controls,
  datePreset,
  detail,
  loading = false,
  management = null,
  managementBusyAction = '',
  managementFeedback = null,
  managementStatus = '',
  mutation,
  publishProgress = null,
  navigate,
  onArchive,
  onBudgetChange,
  onDatePresetChange,
  onDelete,
  onMutation,
  onRetryManagement,
  onRetryPublish,
  onRecoverMetaAccess,
  onRefresh,
  onRestore,
  productPolicy,
}) {
  const detailScope = `${companyId}:${detail.campaign?.id || ''}`;
  const [navigation, setNavigation] = useState({ scope: '', tab: 'overview', adsVisited: false });
  const tabs = [
    ['overview', 'Overview'], ['ads', 'Ads & creatives'],
    ['configuration', 'Configuration'], ['activity', 'Activity & approval'],
  ];
  const selectedTab = navigation.scope === detailScope ? navigation.tab : 'overview';
  const adsVisited = navigation.scope === detailScope && navigation.adsVisited;
  const tabButtons = useRef([]);
  const [settingsScope, setSettingsScope] = useState('');
  const settingsOpen = settingsScope === detailScope;
  const successToast = useCampaignSuccessToast(detailScope, mutation, managementFeedback);
  useEffect(() => {
    if (mutation?.action === 'budget' && mutation?.status === 'success') setSettingsScope('');
  }, [mutation?.action, mutation?.status]);
  const selectTab = (tab) => setNavigation((current) => ({
    scope: detailScope,
    tab,
    adsVisited: tab === 'ads' || (current.scope === detailScope && current.adsVisited),
  }));
  const handleTabKey = (event, index) => {
    const next = event.key === 'ArrowRight' ? (index + 1) % tabs.length
      : event.key === 'ArrowLeft' ? (index + tabs.length - 1) % tabs.length
        : event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : null;
    if (next === null) return;
    event.preventDefault();
    selectTab(tabs[next][0]);
    tabButtons.current[next]?.focus();
  };
  const campaign = detail.campaign || {};
  const configuration = detail.configuration || {};
  const rawMetaTarget = Object.hasOwn(detail, 'metaTarget')
    ? detail.metaTarget
    : configuration.metaTarget;
  const targetProjectionPresent = rawMetaTarget != null;
  const metaTarget = normalizeMetaCampaignTargetPresentation(rawMetaTarget);
  const provider = detail.provider || {};
  const metricsEnvelope = detail.metrics || {};
  const metrics = metricMap(detail);
  const account = metricsEnvelope.account || {};
  const rawCurrency = exactText(account.currency, exactText(configuration.budget?.currency)).toUpperCase();
  const currency = /^[A-Z]{3}$/.test(rawCurrency) ? rawCurrency : '';
  const webhookLeads = webhookLeadMetric(detail);
  const metaLeads = metaLeadMetric(detail);
  const anyControls = canRetryPublish || Object.values(controls || {}).some(Boolean);
  const reconciliation = provider.reconciliation || {};
  const hierarchy = normalizeCampaignHierarchy(provider.hierarchy);
  const reconciliationStatus = exactText(
    typeof reconciliation === 'string' ? reconciliation : reconciliation.status,
    exactText(provider.reconciliationStatus, exactText(provider.availability, 'unavailable')),
  ).toLowerCase();
  const currentOwnershipFailureCode = [
    reconciliation.errorCode,
    provider.failureCode,
    provider.error?.code,
    provider.errorCode,
    metricsEnvelope.failureCode,
    metricsEnvelope.error?.code,
    metricsEnvelope.errorCode,
  ].map((value) => exactText(value)).find((code) => (
    code.startsWith('meta_campaign_credential_continuity_')
    || /^curated_campaign_(?:provider|publish)_binding_/.test(code)
  ));
  // A current ownership/binding failure blocks today's controls and must not
  // be hidden by an older durable operation failure. The operation remains
  // visible in its own facts below.
  const failureCode = currentOwnershipFailureCode
    || detail.lastOperation?.errorCode
    || detail.lastOperation?.failureCode
    || reconciliation.errorCode
    || provider.failureCode
    || provider.error?.code
    || (typeof provider.error === 'string' ? provider.error : '')
    || provider.errorCode
    || metricsEnvelope.failureCode
    || metricsEnvelope.error?.code
    || metricsEnvelope.errorCode;
  const providerAvailability = exactText(provider.availability).toLowerCase();
  const metricsAvailability = exactText(metricsEnvelope.availability).toLowerCase();
  const metricsFreshness = exactText(metricsEnvelope.freshness?.state).toLowerCase();
  const isOperationalCampaign = [
    'publishing', 'paused', 'active', 'partial', 'failed', 'completed', 'archived',
  ].includes(campaign.status);
  const hierarchyAttention = isOperationalCampaign
    && hierarchyNeedsAttention(hierarchy, campaign.status);
  const attentionFailureCode = curatedCampaignAttentionFailureCode(
    failureCode,
    campaign.status,
    publishProgress?.state,
  );
  const attentionMetricsFreshness = curatedCampaignAttentionMetricsFreshness(
    metricsFreshness,
    campaign.status,
    publishProgress?.state,
  );
  const attentionProviderAvailability = curatedCampaignAttentionProviderAvailability(
    providerAvailability,
    campaign.status,
    publishProgress?.state,
  );
  const attentionMetricsAvailability = curatedCampaignAttentionMetricsAvailability(
    metricsAvailability,
    campaign.status,
    publishProgress?.state,
  );
  const attentionReconciliationStatus = curatedCampaignAttentionReconciliationStatus(
    reconciliationStatus,
    campaign.status,
    publishProgress?.state,
  );
  const showFailure = isOperationalCampaign && (
    ['failed', 'partial'].includes(campaign.status)
    || ['failed', 'partial', 'pending', 'stale', 'unavailable'].includes(attentionReconciliationStatus)
    || ['partial', 'stale', 'unavailable'].includes(attentionProviderAvailability)
    || ['partial', 'stale', 'unavailable'].includes(attentionMetricsAvailability)
    || ['partial', 'stale', 'unavailable'].includes(attentionMetricsFreshness)
    || hierarchyAttention
    || detail.lastOperation?.status === 'failed'
    || Boolean(attentionFailureCode)
  );
  const fallbackAttentionMessage = hierarchyAttention
    ? hierarchyFailureGuidance(hierarchy, campaign.status)
    : ['stale', 'unavailable'].includes(attentionMetricsAvailability) || ['stale', 'unavailable'].includes(attentionMetricsFreshness)
      ? 'Meta performance is temporarily unavailable or stale. Refresh the campaign before relying on these metrics.'
      : ['stale', 'unavailable'].includes(attentionProviderAvailability) || ['stale', 'unavailable'].includes(attentionReconciliationStatus)
        ? 'Meta campaign status is temporarily unavailable or stale. Refresh before using a campaign control.'
        : 'The durable campaign status records an incomplete provider operation. Use an available control to retry safely.';
  const attention = showFailure ? campaignAttentionPresentation({
    fallbackMessage: fallbackAttentionMessage,
    failureCode: attentionFailureCode,
    reconciliationStatus: attentionReconciliationStatus,
  }) : null;
  const period = metricsEnvelope.period || {};
  const attribution = metricsEnvelope.attribution || {};
  const actionMapping = metricsEnvelope.actionMapping || {};
  const freshness = metricsEnvelope.freshness || {};
  const renovationTypes = Array.isArray(configuration.renovationTypes) ? configuration.renovationTypes : [];
  const ads = Array.isArray(configuration.ads) ? configuration.ads : [];
  const structure = configuration.structure || {};
  const meta = configuration.meta || {};
  const targeting = configuration.targeting || {};
  const approval = configuration.approval || detail.approval || {};
  const metaAccessRecoveryState = exactText(detail?.metaAccessRecovery?.state);
  const isPending = mutation?.status === 'pending' || Boolean(managementBusyAction);
  const showPublishProgress = shouldShowCuratedMetaCreationProgress(
    campaign.status,
    publishProgress,
  );
  // OGC-448: while Meta's own regain time is in the future a resume cannot
  // succeed and would spend more of the exhausted budget, so the control
  // waits it out; the progress panel says how long.
  const publishRetryWait = useCuratedMetaPublishRetryWait(publishProgress);
  const attentionDismissal = useDocumentNoticeDismissal(campaignNoticeKey({ companyId, campaignId: campaign.id, kind: 'campaign-attention', reasonCode: failureCode || 'provider-unavailable' }));

  const availableManagement = campaignManagementActionsForPolicy(management, productPolicy);
  const managementLocked = isPending || loading || managementStatus !== 'ready';
  const archived = Boolean(management?.archivedAt);
  const deleteRetryWait = useCampaignDeleteRetryWait(management?.deletion);
  const deleteBlocker = deleteRetryWait ? `Try deleting again in about ${deleteRetryWait} min.` : !availableManagement.delete ? campaignDeleteBlockerMessage(management) : '';
  // OGC-455 tier C: this archive stood on no Meta evidence at all. Every
  // panel that describes Meta's side — creation progress ("paused, no
  // spend", "retry resumes"), the reconnect notice, the pinned-publication
  // note — would claim knowledge we do not have or point at a control an
  // archived campaign does not offer. The page leads with what is true.
  const unobservableArchive = archived && management?.archiveEvidence?.tier === 'unobservable';
  const managementAction = archived ? 'restore' : 'archive';
  const restoreVerificationRequired = !archived && ['required', 'failed'].includes(management?.restoreVerification?.status);
  const canSeeManagementAction = !restoreVerificationRequired && hasNamedTenantAction(productPolicy, `campaign.${managementAction}`);
  const managementActionBlocked = !availableManagement[managementAction]
    ? campaignManagementActionBlocker(managementAction, management?.actions?.[managementAction]?.reasonCode)
    : managementLocked ? 'Wait for current campaign status and controls before continuing.' : '';
  const selectedTypes = renovationTypes.map((type) => typeof type === 'string' ? type : type.name || type.label || type.key || type.id).filter(Boolean);
  const destinationFacts = {
    adAccount: targetProjectionPresent ? metaTarget?.destination.adAccount.label || 'Unavailable' : meta.adAccount?.label || meta.adAccountLabel || selectedResourceSummary(meta.adAccountResourceId, 'Unavailable'),
    instagram: targetProjectionPresent ? metaTarget?.destination.instagramIdentity?.label || (metaTarget ? 'Not selected' : 'Unavailable') : meta.instagramIdentity?.label || meta.instagramLabel || selectedResourceSummary(meta.instagramIdentityResourceId, 'Not selected'),
    form: targetProjectionPresent ? metaTarget?.destination.instantForm.label || 'Unavailable' : meta.instantForm?.label || meta.instantFormLabel || selectedResourceSummary(meta.instantFormResourceId, 'Unavailable'),
    page: targetProjectionPresent ? metaTarget?.destination.page.label || 'Unavailable' : meta.instantForm?.owningPage?.label || (meta.instantFormResourceId ? 'Resolved with selected form at approval' : 'Unavailable'),
  };
  // A tier-C campaign's connection cannot reach it, whatever the cause Meta
  // gave; a Meta-target label ("Original operation") would speak for it.
  // A refreshing continuity is not a verification request: say Refreshing.
  const connectionLabel = unobservableArchive || currentOwnershipFailureCode === CONTINUITY_ORIGINAL_UNPROVABLE_CODE ? 'Not reachable'
    : currentOwnershipFailureCode === CONTINUITY_REFRESHING_CODE ? 'Refreshing'
    : currentOwnershipFailureCode || /^meta_(authorization|reauthorization|connection)_/.test(failureCode || '')
    ? 'Needs verification' : metaTarget?.statusLabel || (providerAvailability === 'available' && reconciliationStatus === 'confirmed' ? 'Verified' : 'Unavailable');
  const compactTarget = campaignTargetArea(targeting);
  const settingsSummary = { settings: {
    status: campaign.status, renovationTypes: selectedTypes, targetArea: compactTarget,
    adAccount: destinationFacts.adAccount, instantForm: destinationFacts.form,
    facebookPage: destinationFacts.page, instagramAccount: destinationFacts.instagram,
    monthlyBudget: { ...configuration.budget, currency, canUpdate: controls.budget === true },
  } };
  const activityEvents = [
    { label: 'Delivery status verified', at: reconciliationStatus === 'confirmed' ? hierarchy?.checkedAt || reconciliation.checkedAt || provider.checkedAt : null },
    { label: detail.lastOperation ? `${operationLabel(detail.lastOperation.type || detail.lastOperation.kind || detail.lastOperation.operationType)} · ${titleCase(detail.lastOperation.status)}` : '', at: detail.lastOperation?.completedAt || detail.lastOperation?.updatedAt },
    { label: 'Campaign published', at: campaign.publishedAt || provider.publishedAt },
    { label: 'Approved', at: approval.approvedAt || campaign.approvedAt, note: 'Exact creative versions locked' },
    { label: 'Submitted for approval', at: approval.requestedAt || approval.submittedAt },
    { label: 'Draft saved', at: campaign.createdAt },
  ].filter((event) => event.label && dateTime(event.at) !== 'Unavailable').sort((a, b) => Date.parse(b.at) - Date.parse(a.at));

  return (<>
    <CampaignDetailHeader campaign={campaign} configuration={configuration} navigate={navigate} archived={archived}
      actions={<>
        <Button variant="ghost" size="sm" type="button" className="bl-campaign-icon-button" style={ICON_BUTTON_STYLE} aria-label="Ad settings" title="Ad settings" onClick={() => setSettingsScope(detailScope)} disabled={loading || isPending}><CampaignIcon name="settings" size={16} /></Button>
        {['activate', 'pause', 'resume'].filter((action) => controls[action]).map((action) => <Button key={action} variant="ghost" size="sm" type="button" className="bl-campaign-icon-button" style={ICON_BUTTON_STYLE} aria-label={ACTIONS[action].verb} title={ACTIONS[action].verb} disabled={loading || isPending} onClick={() => onMutation(action)}><CampaignDetailActionIcon action={action} /></Button>)}
        {canSeeManagementAction && <Button variant="ghost" size="sm" type="button" className="bl-campaign-icon-button" style={ICON_BUTTON_STYLE} aria-label={archived ? 'Restore campaign' : 'Archive campaign'} title={archived ? 'Restore campaign' : 'Archive campaign'} disabled={managementLocked || !availableManagement[managementAction]} aria-describedby={managementActionBlocked ? 'campaign-detail-management-blocker' : undefined} onClick={archived ? onRestore : onArchive}><CampaignDetailActionIcon action={managementAction} /></Button>}
        {archived && hasNamedTenantAction(productPolicy, 'campaign.delete') && <Button variant="ghost" size="sm" type="button" className="bl-campaign-icon-button" style={ICON_BUTTON_STYLE} aria-label="Delete campaign" title={deleteBlocker || 'Delete campaign'} disabled={managementLocked || !availableManagement.delete || Boolean(deleteRetryWait)} aria-describedby={deleteBlocker ? 'campaign-detail-delete-blocker' : undefined} onClick={onDelete}><CampaignDetailActionIcon action="delete" /></Button>}
        {deleteBlocker && archived && <span className="bl-visually-hidden" id="campaign-detail-delete-blocker">{deleteBlocker}</span>}
        {managementActionBlocked && <span className="bl-visually-hidden" id="campaign-detail-management-blocker">{managementActionBlocked}</span>}
      </>} />
    <div className="bl-campaign-operational-detail" aria-busy={loading || isPending ? 'true' : 'false'}>
      {archived && managementStatus === 'ready' && hasNamedTenantAction(productPolicy, 'campaign.delete') && deleteBlocker && !deleteRetryWait && <p className="bl-campaign-readonly-note" role="note">Delete unavailable: {deleteBlocker}</p>}
      <Beacon size="control" active={Boolean(deleteRetryWait) && !isPending} label={`Delete retry available in about ${deleteRetryWait} min.`} slowAfterMs={null} />
      {unobservableArchive && <div className="bl-campaign-operation-feedback is-warning bl-detail-attention" role="note" data-campaign-detail-unobservable>
        <strong>{unobservableArchiveNotice(management?.archiveEvidence).title}</strong><span>{unobservableArchiveNotice(management?.archiveEvidence).body}</span>
      </div>}
      {showPublishProgress && !unobservableArchive && <CuratedMetaPublishProgress companyId={companyId} campaignId={campaign.id} progress={publishProgress} />}
      {showFailure && !unobservableArchive && !attentionDismissal.dismissed && <div className={`bl-campaign-operation-feedback is-warning bl-detail-attention${attentionDismissal.canDismiss ? ' is-dismissible' : ''}`} role="status">
        <strong>{attention.title}</strong><span>{attention.body}</span>
        {metaAccessRecoveryState === 'manager_required' && <span>Ask a company owner or admin to verify campaign access, then refresh this page.</span>}
        {attentionDismissal.canDismiss && <button type="button" className="bl-dismissible-notice-close" aria-label="Dismiss campaign notice" onClick={attentionDismissal.dismiss}><IconClose size={16} aria-hidden="true" focusable="false" /></button>}
      </div>}
      {!['idle', 'success'].includes(mutation?.status) && mutation?.message && !(mutation.action === 'publish' && showPublishProgress) && <div className={`bl-campaign-operation-feedback is-${mutation.status}`} role={mutation.status === 'error' ? 'alert' : 'status'}>
        <strong>{mutation.action === 'publish' && mutation.status === 'warning' ? 'Meta creation in progress'
          : mutation.action === 'recover_meta_access' && mutation.status === 'pending' ? 'Verifying campaign access'
            : mutation.action === 'recover_meta_access' && mutation.status === 'success' ? 'Campaign access verified'
              : mutation.action === 'recover_meta_access' && mutation.status === 'warning' ? 'Campaign access still locked'
                : mutation.action === 'recover_meta_access' && mutation.status === 'error' ? 'Campaign access not verified'
                  : mutation.status === 'pending' ? 'Waiting for provider confirmation'
                    : mutation.status === 'success' ? 'Campaign refreshed' : 'Action not confirmed'}</strong>
        <span>{mutation.message}</span>
      </div>}
      {managementFeedback?.action === 'delete' && ['error', 'warning'].includes(managementFeedback.status) && <div className={`bl-campaign-operation-feedback is-${managementFeedback.status}`} role="alert"><strong>Deletion needs attention</strong><span>{managementFeedback.message}</span><button type="button" disabled={isPending || loading} onClick={onRetryManagement}>Refresh campaign controls</button></div>}
      {successToast && <div className="bl-dismissible-notice bl-campaign-toast is-success" role="status">{successToast.message}</div>}
      <div className="bl-detail-tabs" role="tablist" aria-label="Campaign details">
        {tabs.map(([id, label], index) => <button key={id} type="button" role="tab" id={`campaign-detail-tab-${id}`} aria-controls={`campaign-detail-panel-${id}`} aria-selected={selectedTab === id} tabIndex={selectedTab === id ? 0 : -1} ref={(element) => { tabButtons.current[index] = element; }} onClick={() => selectTab(id)} onKeyDown={(event) => handleTabKey(event, index)}>{label}</button>)}
      </div>
      <div className="bl-detail-tab-panel" role="tabpanel" id="campaign-detail-panel-overview" aria-labelledby="campaign-detail-tab-overview" hidden={selectedTab !== 'overview'}>
        <div className="bl-detail-overview-columns">
          <div className="bl-detail-overview-primary">
            <section className="bl-campaign-operations-card bl-detail-performance-picture" aria-labelledby="campaign-performance-picture-title">
              <div className="bl-campaign-card-heading"><div><div className="bl-kicker">Meta Insights</div><h2 id="campaign-performance-picture-title">The performance picture</h2></div>
                <label className="bl-detail-period"><span className="bl-visually-hidden">Date range</span><Select className="bl-select-block" aria-label="Date range" value={datePreset} onChange={onDatePresetChange} disabled={loading || isPending} options={DATE_PRESETS.map((preset) => ({ value: preset.value, label: preset.label }))} /></label>
              </div>
              <p className="bl-campaign-measure-note">Meta-reported results · {periodLabel(period, datePreset)}</p>
              <p className="bl-detail-period-note">{isOperationalCampaign
                ? 'Totals for the selected period. Missing results are shown as unavailable.'
                : 'Performance will appear once the campaign delivers and Meta reports results.'}</p>
              <section className="bl-campaign-performance bl-detail-performance-summary" aria-labelledby="campaign-performance-title">
                <h2 id="campaign-performance-title" className="bl-visually-hidden">Performance</h2>
                <div className="bl-performance-grid">
                  <MetricCard label="Meta-reported leads" visibleLabel="Meta leads" metric={metaLeads} value={formatCampaignMetric(metaLeads, 'integer')} period={periodLabel(period, datePreset)} />
                  <MetricCard label="Cost per Meta lead" visibleLabel="Cost / lead" moneyCurrency={currency} metric={metrics.costPerLead} value={formatCampaignMetric(metrics.costPerLead, 'money', currency)} period={periodLabel(period, datePreset)} />
                  <MetricCard label="Spend" moneyCurrency={currency} metric={metrics.spend} value={formatCampaignMetric(metrics.spend, 'money', currency)} period={periodLabel(period, datePreset)} />
                  <MetricCard label="CTR" metric={metrics.ctr} value={formatCampaignMetric(metrics.ctr, 'percent')} period={periodLabel(period, datePreset)} />
                </div>
              </section>
              <div className="bl-detail-secondary-metrics">
                <MetricCard label="Impressions" metric={metrics.impressions} value={formatCampaignMetric(metrics.impressions, 'integer')} compact />
                <MetricCard label="Reach" metric={metrics.reach} value={formatCampaignMetric(metrics.reach, 'integer')} compact />
                <MetricCard label="Frequency" metric={metrics.frequency} value={formatCampaignMetric(metrics.frequency, 'frequency')} compact />
              </div>
              <details className="bl-detail-report-context"><summary>Reporting details</summary><dl className="bl-campaign-provenance">
                <div><dt>Source</dt><dd>{titleCase(metrics.spend?.source || metricsEnvelope.source || 'meta_insights')}</dd></div>
                <div><dt>Period</dt><dd>{periodLabel(period, datePreset)}</dd></div>
                <div><dt>Attribution</dt><dd>{attributionLabel(attribution)}</dd></div>
                <div><dt>Lead action</dt><dd>{titleCase(actionMapping.selectedActionType || metricsEnvelope.actionSource)}</dd></div>
                <div><dt>Currency</dt><dd>{currency || 'Unavailable'}</dd></div>
                <div><dt>Account time zone</dt><dd>{exactText(account.timezone, exactText(metricsEnvelope.timezone, 'Unavailable'))}</dd></div>
                <div><dt>Freshness</dt><dd>{freshnessLabel(freshness)}</dd></div>
              </dl></details>
            </section>
            <section className="bl-campaign-operations-card bl-detail-leads" aria-labelledby="campaign-workspace-leads-title">
              <h2 id="campaign-workspace-leads-title">Leads in your workspace</h2>
              <p><span aria-label="Webhook Leads list">{formatCampaignMetric(webhookLeads, 'integer')} ingested lead records</span> · managed separately in Leads.</p>
              <p>Meta-reported conversions and webhook-ingested Leads are separate measures and may not match.</p>
              {metricValue(webhookLeads).availability !== 'available' && <p>{titleCase(metricValue(webhookLeads).availability)} · Webhook-ingested rows</p>}
            </section>
          </div>
          <div className="bl-detail-overview-secondary">
            <section className="bl-campaign-operations-card" aria-labelledby="campaign-controls-title">
              <div className="bl-campaign-card-heading"><div><div className="bl-kicker">Delivery & controls</div><h2 id="campaign-controls-title">Campaign status</h2></div></div>
              <dl className="bl-campaign-facts is-compact">
                <div><dt>Campaign</dt><dd>{statusLabel(campaign.status)}</dd></div>
                <div><dt>Meta connection</dt><dd>{connectionLabel}</dd></div>
                <div><dt>Ad sets</dt><dd>{hierarchy?.confirmed.adSetCount != null ? `${hierarchy.confirmed.adSetCount} confirmed` : isOperationalCampaign ? 'Unavailable' : 'Not created'}</dd></div>
                <div><dt>Ads</dt><dd>{Array.isArray(configuration.ads) ? `${ads.length} selected` : 'Selection unavailable'}{hierarchy?.configured.activeAdCount != null ? ` · ${hierarchy.configured.activeAdCount} enabled` : ' · enabled count unavailable'}</dd></div>
                <div><dt>Budget</dt><dd>{detailMoney(configuration.budget?.monthlyMajor, configuration.budget?.currency || currency)} / month</dd></div>
                <div><dt>Last checked</dt><dd>{dateTime(hierarchy?.checkedAt || reconciliation.checkedAt || provider.checkedAt)}</dd></div>
              </dl>
              <p className="bl-campaign-measure-note">Active describes configuration. Actual delivery can also depend on Meta review and parent status.</p>
              <div className="bl-detail-status-actions">
                <button type="button" className="bl-detail-section-link" disabled={loading || isPending} onClick={onRefresh}>Refresh status</button>
                {metaAccessRecoveryState === 'available' && onRecoverMetaAccess && <button type="button" className="bl-detail-section-link" disabled={loading || isPending} onClick={onRecoverMetaAccess}>Verify campaign access</button>}
                {canRetryPublish && !unobservableArchive && <button type="button" className="bl-detail-section-link" disabled={isPending || publishRetryWait.blocked} onClick={onRetryPublish}>{publishRetryWait.blocked
                  ? `Resume creating in Meta — wait ~${publishRetryWait.minutes} min`
                  : 'Resume creating in Meta — paused'}</button>}
              </div>
              {!anyControls && <p className="bl-campaign-readonly-note">{currentOwnershipFailureCode === CONTINUITY_REFRESHING_CODE
                ? continuityRefreshExhausted
                  ? 'The Meta resource refresh for this campaign is taking longer than expected. Refresh the page to check again, or ask a company owner or admin to open it; the saved campaign is unchanged.'
                  : 'BeyondLeads is refreshing the Meta resources this campaign uses; this page re-checks shortly. If controls do not return, refresh the page or ask a company owner or admin to open it. The saved campaign is unchanged.'
                : currentOwnershipFailureCode === CONTINUITY_ORIGINAL_UNPROVABLE_CODE
                ? 'Campaign controls stay locked here. Pause or change this campaign in Meta Ads Manager, or publish a new campaign to manage it here.'
                : metaAccessRecoveryState === 'available'
                ? 'Campaign controls stay locked until the sealed Meta resources are verified. Use Verify campaign access; verification does not change campaign settings or delivery.'
                : metaAccessRecoveryState === 'manager_required' ? 'Campaign controls stay locked until a company owner or admin verifies the sealed Meta resources. Refresh this page after they finish.'
                  : 'This campaign is read-only for your current role and lifecycle state.'}</p>}
              {canOpenSetup && SETUP_STATES.has(campaign.status) && <button type="button" className="bl-detail-section-link" onClick={() => navigate?.(`curated-campaign/${encodeURIComponent(campaign.id)}${campaign.status === 'draft' ? '' : '?step=review'}`)}>{campaignResumeLabel(campaign.status)} <IconChevron size={14} /></button>}
            </section>
            <section className="bl-campaign-operations-card bl-detail-approved-creatives" aria-labelledby="campaign-approved-creatives-title">
              <h2 id="campaign-approved-creatives-title">Approved creatives</h2>
              <p>{selectedTypes.join(' + ') || 'Renovation types unavailable'} · {ads.length} exact saved ad {ads.length === 1 ? 'version' : 'versions'}</p>
              <button className="bl-detail-section-link" type="button" onClick={() => { selectTab('ads'); tabButtons.current[1]?.focus(); }}>Review ads <IconExternal size={14} /></button>
            </section>
          </div>
        </div>
      </div>
      <div className="bl-detail-tab-panel" role="tabpanel" id="campaign-detail-panel-ads" aria-labelledby="campaign-detail-tab-ads" hidden={selectedTab !== 'ads'}>
        {adsVisited && companyId && <CampaignAds companyId={companyId} campaign={campaign} approvedVersions={ads} datePreset={datePreset} productPolicy={productPolicy} refreshNonce={`${campaign.updatedAt || ''}:${detail.lastOperation?.updatedAt || ''}`} onChanged={onRefresh} />}
        {!ads.length && <p className="bl-campaign-measure-note">No sealed ad versions are available yet.</p>}
      </div>
      <div className="bl-detail-tab-panel" role="tabpanel" id="campaign-detail-panel-configuration" aria-labelledby="campaign-detail-tab-configuration" hidden={selectedTab !== 'configuration'}>
        <section className="bl-campaign-configuration" aria-labelledby="campaign-configuration-title">
          <div className="bl-campaign-card-heading"><div><div className="bl-kicker">{campaign.status === 'draft' ? 'Saved draft' : 'Approved campaign setup'}</div><h2 id="campaign-configuration-title">Campaign configuration</h2></div></div>
          <dl className="bl-campaign-facts">
            <div><dt>Renovation types</dt><dd>{selectedTypes.join(', ') || 'Unavailable'}</dd></div>
            <div><dt>Monthly budget</dt><dd>{detailMoney(configuration.budget?.monthlyMajor, configuration.budget?.currency || currency)} {currency}</dd></div>
            <div><dt>Target area</dt><dd>{compactTarget}</dd></div>
            <div><dt>Destination</dt><dd>Meta Instant Form</dd></div>
            <div><dt>Form</dt><dd>{destinationFacts.form}</dd></div>
            <div><dt>Facebook Page</dt><dd>{destinationFacts.page}</dd></div>
            <div><dt>Instagram</dt><dd>{destinationFacts.instagram}</dd></div>
            <div><dt>Account</dt><dd>{destinationFacts.adAccount}</dd></div>
            <div><dt>Creative selection</dt><dd>{ads.length} exact {campaign.status === 'draft' ? 'saved' : 'approved'} versions</dd></div>
          </dl>
          <button type="button" className="bl-detail-section-link" disabled={loading || isPending} onClick={() => setSettingsScope(detailScope)}><CampaignIcon name="settings" size={15} />Ad settings</button>
        </section>
      </div>
      <div className="bl-detail-tab-panel" role="tabpanel" id="campaign-detail-panel-activity" aria-labelledby="campaign-detail-tab-activity" hidden={selectedTab !== 'activity'}>
        <section className="bl-campaign-operations-card" aria-labelledby="campaign-activity-title">
          <div className="bl-campaign-card-heading"><div><div className="bl-kicker">Campaign history</div><h2 id="campaign-activity-title">Activity & authorization</h2></div></div>
          {activityEvents.length ? <ol className="bl-detail-timeline">{activityEvents.map((event, index) => <li key={`${event.label}:${event.at}:${index}`}><span>{event.label}</span><small>{dateTime(event.at)}{event.note ? ` · ${event.note}` : ''}</small></li>)}</ol> : <p>No dated campaign events are available.</p>}
          <p className="bl-campaign-measure-note">Recorded approval milestones and the latest operation. This is not a complete audit history.</p>
        </section>
        <details className="bl-detail-evidence bl-detail-operational-evidence">
          <summary>Operational evidence & campaign records</summary>
          <div className="bl-detail-evidence-body">
            <h3>Hierarchy & provider evidence</h3>
            <dl className="bl-campaign-facts is-compact">
              <div><dt>Durable status</dt><dd>{statusLabel(campaign.status)}</dd></div>
                <div><dt>Campaign configured</dt><dd>{titleCase(hierarchy?.configured.campaignStatus || provider.configuredStatus || provider.status)}</dd></div>
                <div><dt>Campaign effective / review</dt><dd>{titleCase(hierarchy?.effective.campaignStatus || provider.effectiveStatus)}</dd></div>
                {isOperationalCampaign && <>
                  <div><dt>Expected hierarchy</dt><dd>{hierarchyObjectCountLabel(hierarchy?.expected)}</dd></div>
                  <div><dt>Confirmed hierarchy</dt><dd>{hierarchyObjectCountLabel(hierarchy?.confirmed)}</dd></div>
                  <div><dt>Shared ad set configured</dt><dd>{titleCase(hierarchy?.configured.adSetStatus)}</dd></div>
                  <div><dt>Approved ads configured</dt><dd>{hierarchyConfiguredAdsLabel(hierarchy?.configured)}</dd></div>
                  <div><dt>Child configured readiness</dt><dd>{hierarchy?.configured.childrenReady === true
                    ? 'Ready'
                    : hierarchy?.configured.childrenReady === false
                      ? 'Not ready'
                      : 'Unavailable'}</dd></div>
                  <div><dt>Shared ad set effective / review</dt><dd>{titleCase(hierarchy?.effective.adSetStatus)}</dd></div>
                  <div><dt>Approved ads effective / review</dt><dd>{hierarchyEffectiveAdsLabel(hierarchy?.effective.adStatusCounts)}</dd></div>
                  <div><dt>Approved mapping</dt><dd>{hierarchy?.mappingConfirmed === true
                    ? 'Confirmed'
                    : hierarchy?.mappingConfirmed === false
                      ? 'Not confirmed'
                      : 'Unavailable'}</dd></div>
                  <div><dt>Hierarchy reconciliation</dt><dd>{titleCase(hierarchy?.reconciliationStatus)}</dd></div>
                  <div><dt>Hierarchy evidence updated</dt><dd>{dateTime(hierarchy?.checkedAt)}</dd></div>
                </>}
                <div><dt>Campaign reconciliation</dt><dd>{titleCase(reconciliationStatus)}</dd></div>
                <div><dt>Provider evidence updated</dt><dd>{dateTime(reconciliation.checkedAt || provider.checkedAt || provider.updatedAt)}</dd></div>
                <div><dt>Last campaign update</dt><dd>{dateTime(campaign.updatedAt)}</dd></div>
                <div><dt>Last operation</dt><dd>{detail.lastOperation
                  ? `${operationLabel(detail.lastOperation.type || detail.lastOperation.kind || detail.lastOperation.operationType)} · ${titleCase(detail.lastOperation.status)}`
                  : 'Unavailable'}</dd></div>
                <div><dt>Operation updated</dt><dd>{dateTime(detail.lastOperation?.updatedAt || detail.lastOperation?.completedAt)}</dd></div>

            </dl>
            <p>Configured readiness and Meta effective delivery or review state are separate. An Active campaign does not by itself mean the ads are delivering.</p>
            {metaTarget && !unobservableArchive && <div className="bl-detail-target-evidence"><strong>{metaTarget.title}</strong><p>{metaTarget.body}</p></div>}
            <h3>Saved configuration & approval</h3>
            <dl className="bl-campaign-facts is-compact">
              <div><dt>Configuration revision</dt><dd>{configuration.revision ?? 'Unavailable'}</dd></div>
              <div><dt>Targeting</dt><dd>{targetingLabel(targeting)}</dd></div>
              <div><dt>Structure</dt><dd>{structure.campaignCount ?? '—'} campaign · {structure.adSetCount ?? '—'} ad set · {structure.adCount ?? (ads.length || '—')} ads</dd></div>
              <div><dt>Creation review</dt><dd>{['draft', 'awaiting_approval'].includes(campaign.status) ? 'Ready to review' : titleCase(approval.status || 'authorized')}</dd></div>
              <div><dt>Created</dt><dd>{dateTime(campaign.createdAt)}</dd></div>
              <div><dt>Updated</dt><dd>{dateTime(campaign.updatedAt)}</dd></div>
              <div><dt>Approval requested</dt><dd>{dateTime(approval.requestedAt || approval.submittedAt)}</dd></div>
              <div><dt>Approved</dt><dd>{dateTime(approval.approvedAt || campaign.approvedAt)}</dd></div>
              <div><dt>Published</dt><dd>{dateTime(campaign.publishedAt || provider.publishedAt)}</dd></div>
            </dl>
            <details className="bl-detail-evidence"><summary>Exact approved asset versions</summary><div className="bl-campaign-assets">{ads.length ? ads.map((ad, index) => <article key={ad.creativeVersionId || index}><strong>{ad.renovationTypeName || ad.renovationTypeKey || `Ad ${index + 1}`}</strong><span>{ad.headline || 'Curated ad'}</span><code>{ad.creativeVersionId || 'Version unavailable'}</code></article>) : <p>No sealed ad versions are available yet.</p>}</div></details>
        {managementStatus === 'loading' && (
          <div className="bl-campaign-management-loading"><Beacon size="control" tone="neutral" label={management
            ? 'Refreshing archive and deletion eligibility…'
            : 'Checking archive and deletion eligibility…'} delayMs={0} slowAfterMs={null} /></div>
        )}
        {managementStatus === 'error' && (
          <div className="bl-campaign-management-feedback is-error" role="alert">
            <span>{management
              ? 'The latest campaign management state is unavailable. Previously loaded controls are read-only until refreshed.'
              : 'Campaign management is temporarily unavailable.'}</span>
            {onRetryManagement && <button type="button" disabled={loading} onClick={onRetryManagement}>Retry campaign controls</button>}
          </div>
        )}
        {management && (
          <CampaignManagementControls
            busyAction={managementBusyAction
              || (managementStatus === 'loading' ? 'refresh' : '')
              || (managementStatus === 'error' ? 'stale' : '')}
            canonicalDetail
            feedback={managementFeedback?.status === 'success' ? null : managementFeedback}
            management={management}
            onArchive={onArchive}
            onDelete={onDelete}
            onRetry={managementStatus === 'error' ? undefined : onRetryManagement}
            onRestore={onRestore}
            productPolicy={productPolicy}
          />
        )}



          </div>
        </details>
      </div>
      {settingsOpen && <CampaignSettingsDrawer title="Ad settings" description={campaign.name} busy={isPending} confirmationOpen={confirmationOpen} onClose={() => setSettingsScope('')}
        footer={<><button type="button" disabled={isPending} onClick={() => { setSettingsScope(''); selectTab('configuration'); tabButtons.current[2]?.focus(); }}>Open details</button>{controls.budget && <button type="submit" form="curated-campaign-budget-form" className="is-yellow" disabled={isPending || budgetAccess?.allowed === false}>Review budget change</button>}</>}>
        {budgetNotice && <div className="bl-dismissible-notice bl-campaign-toast bl-campaign-panel-notice is-success" role="status">{budgetNotice.message}</div>}
        <CampaignSettings campaign={{ ...campaign, archivedAt: management?.archivedAt }} summary={settingsSummary} budgetValue={budget} budgetBusy={isPending} budgetInputId="curated-campaign-monthly-budget" budgetFeedback={!budgetNotice && mutation?.action === 'budget' ? mutation : null} onBudgetChange={onBudgetChange} onBudgetSubmit={() => onMutation('budget')} productPolicy={productPolicy} formId="curated-campaign-budget-form" showSubmit={false} budgetAccess={budgetAccess} onViewBilling={() => navigate?.('billing')} onRefreshBudget={onRefresh} />
      </CampaignSettingsDrawer>}
    </div>
  </>);
}

function MetricCard({ label, visibleLabel, metric, value, source, period, compact = false, moneyCurrency }) {
  const normalized = metricValue(metric);
  const sourceLabel = source || titleCase(normalized.source);
  const currencyUnavailable = moneyCurrency !== undefined && !/^[A-Z]{3}$/.test(moneyCurrency);
  const availability = currencyUnavailable ? 'unavailable' : normalized.availability;
  const unavailableLabel = currencyUnavailable ? 'Currency unavailable' : titleCase(availability);
  const missing = normalized.value === null || availability === 'not_applicable' || currencyUnavailable;
  const displayedValue = missing ? '—' : value;
  const separator = new Intl.NumberFormat(undefined, { useGrouping: true })
    .formatToParts(1000000).find((part) => part.type === 'group')?.value;
  const groups = typeof displayedValue === 'string' && separator
    ? displayedValue.split(separator).map((group, index, parts) => index < parts.length - 1 ? group + separator : group)
    : [displayedValue];
  return (
    <div className={`bl-performance-metric is-${availability}`} role="group" aria-label={label}>
      <span aria-label={visibleLabel ? label : undefined}>{visibleLabel || label}</span>
      <strong aria-label={missing ? `${label} · ${sourceLabel} · ${unavailableLabel}` : undefined}>{groups.flatMap((group, index) => index ? [<wbr key={index} />, group] : [group])}</strong>
      <small role="note" className={compact && availability === 'available' ? 'bl-visually-hidden' : undefined} aria-label={`${sourceLabel} · ${unavailableLabel}`}>{period && availability === 'available' ? period : `${sourceLabel} · ${unavailableLabel}`}</small>
    </div>
  );
}

function useCampaignSuccessToast(scope, mutation, management) {
  const [toast, setToast] = useState(null);
  useEffect(() => {
    if (mutation?.status !== 'success' || !mutation.message) {
      setToast((current) => current?.owner === 'provider' ? null : current);
      return undefined;
    }
    const next = { scope, owner: 'provider', message: mutation.message };
    setToast(next);
    const timer = setTimeout(() => setToast((current) => current === next ? null : current), 5000);
    return () => clearTimeout(timer);
  }, [scope, mutation?.status, mutation?.message]);
  useEffect(() => {
    if (management?.status !== 'success' || !management.message) {
      setToast((current) => current?.owner === 'management' ? null : current);
      return undefined;
    }
    const next = { scope, owner: 'management', message: management.message };
    setToast(next);
    const timer = setTimeout(() => setToast((current) => current === next ? null : current), 5000);
    return () => clearTimeout(timer);
  }, [scope, management?.status, management?.message]);
  return toast?.scope === scope ? toast : null;
}

function campaignTargetArea(targeting = {}) {
  const cities = Array.isArray(targeting.includedCities) ? targeting.includedCities : [];
  const labels = cities.map((city) => typeof city === 'string' ? city : city.label || city.name).filter(Boolean);
  const keys = Array.isArray(targeting.includedCityKeys) ? targeting.includedCityKeys.filter(Boolean) : [];
  const location = labels.join(', ') || (keys.length ? `Included city identifiers: ${keys.join(', ')}` : exactText(targeting.country, 'Target area unavailable'));
  const radius = targeting.radius?.value !== undefined ? ` + ${targeting.radius.value} ${targeting.radius.unit || ''}`.trimEnd() : '';
  return `${location}${radius}`;
}

function detailMoney(value, currency) {
  if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value)) || !/^[A-Z]{3}$/.test(currency || '')) return 'Unavailable';
  return number(Number(value), { style: 'currency', currency });
}

// OGC-445 ghost icon squares keep their sizes in CampaignDetails.css.
const ICON_BUTTON_STYLE = Object.freeze({ padding: 0, justifyContent: 'center' });

function CampaignDetailActionIcon({ action }) {
  const name = ['budget', 'recover_meta_access', 'publish'].includes(action) ? 'settings' : action === 'activate' ? 'resume' : action || 'settings';
  return <CampaignIcon name={name} size={17} />;
}

function selectedResourceSummary(resourceId, fallback) {
  return exactText(resourceId) ? `Selected resource: ${resourceId}` : fallback;
}

function periodLabel(period, fallbackPreset) {
  const resolved = period?.resolved || {};
  if (resolved.since || resolved.until) return `${resolved.since || '…'} – ${resolved.until || '…'}`;
  const requested = period?.requested || {};
  if (requested.since || requested.until) return `${requested.since || '…'} – ${requested.until || '…'}`;
  const preset = requested.datePreset || period.datePreset || fallbackPreset;
  return DATE_PRESETS.find(({ value }) => value === preset)?.label || 'Unavailable';
}

function attributionLabel(attribution) {
  if (attribution.reportedSetting) return titleCase(attribution.reportedSetting);
  const windows = Array.isArray(attribution.requestedWindows) ? attribution.requestedWindows : [];
  if (windows.length) return windows.map(titleCase).join(', ');
  return titleCase(attribution.window || attribution.source);
}

function freshnessLabel(freshness) {
  const state = titleCase(freshness.state || freshness.status);
  const time = dateTime(freshness.retrievedAt || freshness.asOf);
  return time === 'Unavailable' ? state : `${state} · ${time}`;
}

function targetingLabel(targeting) {
  const included = Array.isArray(targeting.includedCities) ? targeting.includedCities : [];
  const excluded = Array.isArray(targeting.excludedCities) ? targeting.excludedCities : [];
  const includedKeys = Array.isArray(targeting.includedCityKeys) ? targeting.includedCityKeys.filter(Boolean) : [];
  const excludedKeys = Array.isArray(targeting.excludedCityKeys) ? targeting.excludedCityKeys.filter(Boolean) : [];
  const country = targeting.country || 'Country unavailable';
  const includedLabels = included.map((city) => typeof city === 'string' ? city : city.label || city.name).filter(Boolean);
  const excludedLabels = excluded.map((city) => typeof city === 'string' ? city : city.label || city.name).filter(Boolean);
  const includedText = includedLabels.length
    ? `Included cities: ${includedLabels.join(', ')}`
    : includedKeys.length
      ? `Included city identifiers: ${includedKeys.join(', ')}`
      : 'No included cities';
  const excludedText = excludedLabels.length
    ? `Excluded cities: ${excludedLabels.join(', ')}`
    : excludedKeys.length
      ? `Excluded city identifiers: ${excludedKeys.join(', ')}`
      : 'No excluded cities';
  const radius = targeting.radius?.value !== undefined ? `${targeting.radius.value} ${targeting.radius.unit || ''}`.trim() : 'radius unavailable';
  return `${country} · ${includedText} · ${excludedText} · ${radius}`;
}

function LockedReportsNotice() {
  return (
    <div className="bl-advanced-reports">
      <strong>Advanced reports · Coming Soon</strong>
      <span>Breakdowns, trends, and deeper performance reporting remain locked for this MVP.</span>
    </div>
  );
}

function CampaignNotice({ title, body, role, actionLabel = '', loading = false, onAction }) {
  return (
    <div
      className="bl-campaign-list-notice"
      role={loading ? 'status' : role}
      aria-live={loading ? 'polite' : undefined}
      aria-busy={loading ? 'true' : undefined}
    >
      {loading && <Beacon size="page" delayMs={0} />}
      <strong>{title}</strong>
      <span>{body}</span>
      {actionLabel && onAction && <button type="button" onClick={onAction}>{actionLabel}</button>}
    </div>
  );
}
